// =====================================================
// src/pages/Organisaties.js
// vORG-023
//
// Opmerking:
// Dit bestand is alleen opnieuw geordend en voorzien van
// extra commentaar / sectiekoppen.
// De functionele code is bewust ongemoeid gelaten.
//
// Bezoekadres -> Correspondentieadres auto-kopie:
// - NIEUW: bezoekadres vult direct correspondentie,
//   totdat je corr-velden zelf aanraakt.
// - BESTAAND: bezoekadres vult corr alleen als corr-veld
//   leeg is (nooit overschrijven).
// - FIX: gewone gebruikers krijgen bij eerste laadactie direct
//   user-id en bedrijfscode-scope mee, zodat het grid niet leeg start.
// =====================================================

import React, { useEffect, useMemo, useRef, useState } from "react";
import useVoiceCommands from "../components/useVoiceCommands";

// =====================================================
// SECTION 1: ALGEMENE HELPERS
// Kleine hulpfuncties voor normaliseren, trimmen,
// sorteren en eenvoudige tekstverwerking.
// =====================================================
function getParam(name) {
  try {
    const url = new URL(window.location.href);
    return url.searchParams.get(name) || "";
  } catch {
    return "";
  }
}

function norm(s) {
  return String(s || "")
    .toLowerCase()
    .trim()
    .replace(/[，、]/g, ",")
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/[;:!?]/g, "")
    .replace(/\s+/g, " ");
}

function safeTrim(s) {
  return String(s || "").trim();
}

function joinSp(parts, sep = " ") {
  return parts.map((x) => safeTrim(x)).filter(Boolean).join(sep).trim();
}

function isEmptyWord(t) {
  const x = norm(t);
  return x === "geen" || x === "blanco" || x === "leeg" || x === "null";
}

function uniqSorted(arr) {
  const set = new Set((arr || []).map((x) => String(x).trim()).filter(Boolean));
  return Array.from(set).sort((a, b) => a.localeCompare(b, "nl"));
}

function isBlank(v) {
  return safeTrim(v) === "";
}

function formatCurrencyNL(value) {
  const n = Number(String(value ?? "").replace(",", "."));

  if (!Number.isFinite(n)) return "-";

  return n.toLocaleString("nl-NL", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatDateNL(iso) {
  const s = String(iso || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return "";
  const [y, m, d] = s.split("-");
  return `${d}-${m}-${y}`;
}

// =====================================================
// SECTION 2: LOKALE KEUZELIJSTEN (localStorage)
// Ophalen / bewaren van vrije invoer-opties voor
// keuzelijsten, zodat nieuwe waarden beschikbaar blijven.
// =====================================================
function loadLocalList(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter(Boolean).map((x) => String(x)) : [];
  } catch {
    return [];
  }
}

function saveLocalList(key, arr) {
  try {
    const clean = Array.from(new Set((arr || []).map((x) => String(x).trim()).filter(Boolean)));
    localStorage.setItem(key, JSON.stringify(clean));
  } catch {}
}

function ensureOption(key, value) {
  const v = String(value || "").trim();
  if (!v) return;
  const cur = loadLocalList(key);
  if (!cur.some((x) => norm(x) === norm(v))) {
    saveLocalList(key, [...cur, v]);
  }
}

// =====================================================
// SECTION 3: API HELPER
// Probeert één of meer URL's, voegt tenant + token toe
// en retourneert JSON of tekst.
// =====================================================

async function tryFetchJson(urls, options = {}) {
  let lastErr = null;

  const token =
    sessionStorage.getItem("token") ||
    localStorage.getItem("token") ||
    "";

  const tenant =
    sessionStorage.getItem("tenant") ||
    localStorage.getItem("tenant") ||
    "demo";

  let parsedUser = {};
  let role = "user";
  try {
    parsedUser = JSON.parse(
      sessionStorage.getItem("user") ||
      localStorage.getItem("user") ||
      "{}"
    );
    role = parsedUser?.role || "user";
  } catch {
    parsedUser = {};
  }

  const companyCodeIds =
    sessionStorage.getItem("staffiq_company_code_ids") ||
    localStorage.getItem("staffiq_company_code_ids") ||
    "";

  for (const url of urls) {
    try {
      const headers = {
        ...(options.headers || {}),
        Authorization: token ? `Bearer ${token}` : undefined,
        "x-tenant": tenant,
        "x-role": role,
        "x-staffiq-user-id": parsedUser?.id || undefined,
        "x-staffiq-user-email": parsedUser?.email || undefined,
        "x-staffiq-user-name": parsedUser?.display_name || parsedUser?.name || undefined,
        "x-company-code-ids": companyCodeIds || undefined,
      };

      Object.keys(headers).forEach(
        (k) => headers[k] === undefined && delete headers[k]
      );

      const res = await fetch(url, {
        ...options,
        headers,
      });

      if (!res.ok) {
        const raw = await res.text();
        let msg = `${url} (${res.status})`;

        try {
          const data = raw ? JSON.parse(raw) : null;
          msg = data?.error || data?.message || raw || msg;
        } catch {
          msg = raw || msg;
        }

        throw new Error(msg);
      }

      if (res.status === 204) return null;

      const text = await res.text();
      if (!text) return null;

      try {
        return JSON.parse(text);
      } catch {
        return text;
      }
    } catch (err) {
      lastErr = err;
    }
  }

  throw lastErr;
}

function normalizePhoneForCommunication(raw, defaultCountryCode = "+31") {
  let s = String(raw || "").trim();

  if (!s || s === "-") return "";

  s = s
    .replace(/[^\d+]/g, "")
    .replace(/^00/, "+");

  if (!s) return "";

  if (s.startsWith("+")) return s;

  if (s.startsWith("0")) {
    return `${defaultCountryCode}${s.slice(1)}`;
  }

  if (s.startsWith("31")) {
    return `+${s}`;
  }

  return `${defaultCountryCode}${s}`;
}

async function loadCommunicationSettingsSafe() {
  try {
    const data = await tryFetchJson(["/api/communication-settings"]);

    return {
      email_enabled: data?.email_enabled !== false,
      whatsapp_enabled: data?.whatsapp_enabled !== false,
      sms_enabled: data?.sms_enabled === true,
      default_country_code: data?.default_country_code || "+31",
      default_sms_text: data?.default_sms_text || "",
      default_whatsapp_text: data?.default_whatsapp_text || "",
    };
  } catch {
    return {
      email_enabled: true,
      whatsapp_enabled: true,
      sms_enabled: false,
      default_country_code: "+31",
      default_sms_text: "",
      default_whatsapp_text: "",
    };
  }
}

function stopGridClick(e) {
  try {
    e.preventDefault();
    e.stopPropagation();
  } catch {}
}

function askCommunicationChoice(raw) {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");

    overlay.style.position = "fixed";
    overlay.style.left = "0";
    overlay.style.top = "0";
    overlay.style.right = "0";
    overlay.style.bottom = "0";
    overlay.style.background = "rgba(0,0,0,0.35)";
    overlay.style.zIndex = "999999";
    overlay.style.display = "flex";
    overlay.style.alignItems = "center";
    overlay.style.justifyContent = "center";
    overlay.style.padding = "18px";
    overlay.style.boxSizing = "border-box";

    const box = document.createElement("div");

    box.style.width = "min(520px, 100%)";
    box.style.background = "#fff";
    box.style.borderRadius = "12px";
    box.style.boxShadow = "0 18px 50px rgba(0,0,0,0.35)";
    box.style.padding = "18px";
    box.style.boxSizing = "border-box";
    box.style.fontFamily = "Arial, Helvetica, sans-serif";
    box.style.color = "#111";

    const title = document.createElement("div");
    title.style.fontWeight = "700";
    title.style.fontSize = "18px";
    title.style.marginBottom = "12px";
    title.textContent = `Wat wil je doen met ${raw}?`;

    const help = document.createElement("div");
    help.style.fontSize = "16px";
    help.style.lineHeight = "1.8";
    help.style.marginBottom = "12px";
    help.innerHTML = "1 = Bellen<br/>2 = SMS<br/>3 = WhatsApp";

    const input = document.createElement("input");
    input.type = "text";
    input.value = "3";
    input.inputMode = "numeric";
    input.autocomplete = "off";
    input.style.width = "100%";
    input.style.fontSize = "20px";
    input.style.padding = "10px 12px";
    input.style.border = "2px solid #1f4e79";
    input.style.borderRadius = "8px";
    input.style.boxSizing = "border-box";
    input.style.marginBottom = "14px";

    const buttons = document.createElement("div");
    buttons.style.display = "flex";
    buttons.style.gap = "10px";
    buttons.style.justifyContent = "flex-end";

    const okButton = document.createElement("button");
    okButton.type = "button";
    okButton.textContent = "OK";
    okButton.style.padding = "10px 18px";
    okButton.style.border = "none";
    okButton.style.borderRadius = "8px";
    okButton.style.background = "#1f4e79";
    okButton.style.color = "#fff";
    okButton.style.fontSize = "16px";
    okButton.style.cursor = "pointer";

    const cancelButton = document.createElement("button");
    cancelButton.type = "button";
    cancelButton.textContent = "Annuleren";
    cancelButton.style.padding = "10px 18px";
    cancelButton.style.border = "1px solid #d0d0d0";
    cancelButton.style.borderRadius = "8px";
    cancelButton.style.background = "#fff";
    cancelButton.style.color = "#111";
    cancelButton.style.fontSize = "16px";
    cancelButton.style.cursor = "pointer";

    let done = false;

    function cleanup(value) {
      if (done) return;
      done = true;

      try {
        document.body.removeChild(overlay);
      } catch {}

      resolve(value);
    }

    okButton.onclick = () => cleanup(input.value);
    cancelButton.onclick = () => cleanup(null);

    input.onkeydown = (ev) => {
      if (ev.key === "Enter") {
        ev.preventDefault();
        cleanup(input.value);
      }

      if (ev.key === "Escape") {
        ev.preventDefault();
        cleanup(null);
      }
    };

    overlay.onclick = (ev) => {
      if (ev.target === overlay) {
        cleanup(null);
      }
    };

    buttons.appendChild(cancelButton);
    buttons.appendChild(okButton);

    box.appendChild(title);
    box.appendChild(help);
    box.appendChild(input);
    box.appendChild(buttons);

    overlay.appendChild(box);
    document.body.appendChild(overlay);

    setTimeout(() => {
      try {
        input.focus();
        input.select();
      } catch {}
    }, 80);
  });
}

async function handleEmailGridClick(e, email) {
  stopGridClick(e);

  const mail = String(email || "").trim();

  if (!mail || mail === "-") return;

  const settings = await loadCommunicationSettingsSafe();

  if (settings.email_enabled === false) {
    alert("E-mail is niet actief in Communicatie-instellingen.");
    return;
  }

  window.location.href = `mailto:${encodeURIComponent(mail)}`;
}

function handlePhoneGridClick(e, phone) {
  stopGridClick(e);

  const tel = String(phone || "").trim();

  if (!tel || tel === "-") return;

  window.location.href = `tel:${tel}`;
}

async function handleMobileGridClick(e, mobile) {
  stopGridClick(e);

  const raw = String(mobile || "").trim();

  if (!raw || raw === "-") return;

  const settings = await loadCommunicationSettingsSafe();
  const normalized = normalizePhoneForCommunication(raw, settings.default_country_code || "+31");

  if (!normalized) return;

  const choice = await askCommunicationChoice(raw);

  const c = String(choice || "").trim().toLowerCase();

  if (!c) return;

  if (c === "1" || c === "bellen" || c === "bel") {
    window.location.href = `tel:${raw}`;
    return;
  }

  if (c === "2" || c === "sms") {
    if (settings.sms_enabled !== true) {
      alert("SMS is niet actief in Communicatie-instellingen.");
      return;
    }

    const text = window.prompt(
      `SMS naar ${raw}`,
      settings.default_sms_text || ""
    );

    if (text === null) return;

    const message = String(text || "").trim();

    if (!message) {
      alert("SMS tekst ontbreekt.");
      return;
    }

    try {
      await tryFetchJson(["/api/communications/sms"], {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          to: raw,
          text: message,
        }),
      });

      alert(`SMS verstuurd naar ${raw}`);
    } catch (err) {
      alert(err?.message || "SMS verzenden mislukt.");
    }

    return;
  }

  if (c === "3" || c === "whatsapp" || c === "app") {
    if (settings.whatsapp_enabled === false) {
      alert("WhatsApp is niet actief in Communicatie-instellingen.");
      return;
    }

    const whatsappNumber = normalized.replace(/[^\d]/g, "");
    const text = String(settings.default_whatsapp_text || "").trim();
    const suffix = text ? `?text=${encodeURIComponent(text)}` : "";

    window.open(`https://wa.me/${whatsappNumber}${suffix}`, "_blank", "noopener,noreferrer");
  }
}

// =====================================================
// SECTION 4: ACTIVE / BOOL PARSING
// Ondersteunt meerdere naamvarianten uit backend-data.
// =====================================================
function parseActiveValue(it) {
  const raw = it?.active ?? it?.is_active ?? it?.isActive ?? it?.actief;
  if (raw === undefined || raw === null || raw === "") return null;
  if (raw === true) return true;
  if (raw === false) return false;
  const s = String(raw).toLowerCase().trim();
  if (raw === 1 || s === "1" || s === "true" || s === "yes" || s === "y" || s === "j" || s === "ja" || s === "aan" || s === "on") return true;
  if (raw === 0 || s === "0" || s === "false" || s === "no" || s === "n" || s === "nee" || s === "uit" || s === "off") return false;
  return !!raw;
}

function activeToBool(it) {
  const v = parseActiveValue(it);
  return v === null ? true : !!v;
}

// =====================================================
// SECTION 5: FORMATTERING EN INVOER-SCHOONMAAK
// Naamopmaak, cijferfilters, decimalen en leestekens.
// =====================================================
function titleCaseWord(w) {
  const t = safeTrim(w);
  if (!t) return "";

  // volledig hoofdletters: laten staan
  if (t === t.toUpperCase()) return t;

  // gemengde hoofdletters zoals StaffIQ, iPhone, McDonald’s: laten staan
  if (/[A-Z]/.test(t.slice(1))) return t;

  // normale woorden: alleen eerste letter hoofdletter
  return t[0].toUpperCase() + t.slice(1).toLowerCase();
}

function formatNameLike(s) {
  // Alleen opschonen, GEEN hoofdletters aanpassen
  const raw = safeTrim(s);
  if (!raw) return "";

  return raw.replace(/\s+/g, " ").trim();
}

function onlyDigitsDash(s) {
  return String(s || "").replace(/[^\d-]/g, "");
}

function intOrEmptyString(s) {
  const t = String(s || "").replace(/[^\d]/g, "");
  return t;
}

function decimal2OrEmptyString(s) {
  const raw = String(s || "").trim();
  if (!raw) return "";
  const cleaned = raw.replace(/,/g, ".").replace(/[^\d.]/g, "");
  const parts = cleaned.split(".");
  const a = parts[0] || "";
  const b = (parts[1] || "").slice(0, 2);
  if (parts.length === 1) return a;
  return `${a}.${b}`;
}

function decimalPercentOrEmptyString(s) {
  const raw = String(s || "").trim();
  if (!raw) return "";
  const cleaned = raw.replace(/,/g, ".").replace(/[^\d.]/g, "");
  const parts = cleaned.split(".");
  const a = parts[0] || "";
  const b = (parts[1] || "").slice(0, 2);
  if (parts.length === 1) return a;
  return `${a}.${b}`;
}

function parseDecimalNumber(value) {
  const raw = String(value ?? "")
    .trim()
    .replace(/\s/g, "")
    .replace(/[^0-9,.-]/g, "");

  if (!raw) return null;

  let cleaned = raw;

  if (cleaned.includes(",")) {
    cleaned = cleaned.replace(/\./g, "").replace(",", ".");
  } else {
    const dotCount = (cleaned.match(/\./g) || []).length;
    if (dotCount > 1) cleaned = cleaned.replace(/\./g, "");
  }

  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

function formatDecimalForStorage(value, decimals = 2) {
  const n = typeof value === "number" ? value : parseDecimalNumber(value);
  if (!Number.isFinite(n)) return "";
  return n.toFixed(decimals);
}

function punctToChar(t) {
  const x = norm(t);
  if (x === "punt") return ".";
  if (x === "komma") return ",";
  if (x === "dubbele punt" || x === "dubbelpunt") return ":";
  if (x === "puntkomma") return ";";
  if (x === "vraagteken") return "?";
  if (x === "uitroepteken") return "!";
  if (x === "nieuwe regel" || x === "enter") return "\n";
  return null;
}

// =====================================================
// SECTION 6: DATUM PARSING (nl-NL)
// Zet gesproken / vrije datum-invoer om naar ISO-formaat.
// =====================================================
function parseDutchDateToISO(raw) {
  const s0 = safeTrim(raw);
  if (!s0) return "";

  const digits = s0.replace(/[^\d]/g, "");

  if (digits.length === 8) {
    const dd = parseInt(digits.slice(0, 2), 10);
    const mm = parseInt(digits.slice(2, 4), 10);
    const yyyy = parseInt(digits.slice(4, 8), 10);
    if (yyyy >= 1900 && yyyy <= 2100 && mm >= 1 && mm <= 12 && dd >= 1 && dd <= 31) {
      const iso = `${String(yyyy).padStart(4, "0")}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
      const d = new Date(`${iso}T00:00:00`);
      if (!Number.isNaN(d.getTime())) return iso;
    }
  }

  if (digits.length === 6) {
    const dd = parseInt(digits.slice(0, 2), 10);
    const mm = parseInt(digits.slice(2, 4), 10);
    const yy = parseInt(digits.slice(4, 6), 10);
    const yyyy = 2000 + yy;
    if (yyyy >= 1900 && yyyy <= 2100 && mm >= 1 && mm <= 12 && dd >= 1 && dd <= 31) {
      const iso = `${String(yyyy).padStart(4, "0")}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
      const d = new Date(`${iso}T00:00:00`);
      if (!Number.isNaN(d.getTime())) return iso;
    }
  }

  const s = norm(s0).replace(/[\.\,]/g, " ");
  const tokens = s.split(" ").filter(Boolean);

  const monthMap = {
    jan: 1,
    januari: 1,
    feb: 2,
    februari: 2,
    mrt: 3,
    maart: 3,
    apr: 4,
    april: 4,
    mei: 5,
    jun: 6,
    juni: 6,
    jul: 7,
    juli: 7,
    aug: 8,
    augustus: 8,
    sep: 9,
    sept: 9,
    september: 9,
    okt: 10,
    oktober: 10,
    nov: 11,
    november: 11,
    dec: 12,
    december: 12,
  };

  const splitMore = [];
  for (const t of tokens) {
    const parts = t.split(/[-/]/g).filter(Boolean);
    splitMore.push(...parts);
  }
  const tt = splitMore.filter(Boolean);

  if (tt.length >= 2) {
    const d1 = parseInt(tt[0], 10);
    const mWord = tt[1];
    const m1 = monthMap[mWord];

    if (Number.isFinite(d1) && m1) {
      let y1 = null;
      if (tt.length >= 3) {
        const yTry = parseInt(tt[2], 10);
        if (Number.isFinite(yTry)) y1 = yTry;
      }
      if (!y1) y1 = new Date().getFullYear();
      if (y1 < 100) y1 = 2000 + y1;

      if (y1 >= 1900 && y1 <= 2100 && d1 >= 1 && d1 <= 31) {
        const iso = `${String(y1).padStart(4, "0")}-${String(m1).padStart(2, "0")}-${String(d1).padStart(2, "0")}`;
        const d = new Date(`${iso}T00:00:00`);
        if (!Number.isNaN(d.getTime())) return iso;
      }
    }
  }

  if (tt.length >= 3) {
    const dd = parseInt(tt[0], 10);
    const mm = parseInt(tt[1], 10);
    let yyyy = parseInt(tt[2], 10);

    if (Number.isFinite(yyyy) && yyyy < 100) yyyy = 2000 + yyyy;

    if (yyyy >= 1900 && yyyy <= 2100 && mm >= 1 && mm <= 12 && dd >= 1 && dd <= 31) {
      const iso = `${String(yyyy).padStart(4, "0")}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
      const d = new Date(`${iso}T00:00:00`);
      if (!Number.isNaN(d.getTime())) return iso;
    }
  }

  return "";
}

// =====================================================
// SECTION 7: EMPTY FORM MODEL
// Startstructuur van het formulier voor nieuwe records.
// =====================================================
const emptyForm = {
  id: null,

  relation_entity_type: "company",
  company_code_id: "",

  first_name: "",
  initials: "",
  name_prefix: "",
  last_name: "",
  gender: "",
  birth_date: "",
  salutation: "",
  attention_to: "",

  name: "",
  search_name: "",
  parent_organisation_id: "",

  type: "",
  administration_type: "",
  first_contact_date: "",

  phone: "",
  mobile: "",
  email: "",
  website: "",

  // bezoekadres
  visit_street: "",
  visit_house_number: "",
  visit_addition: "",
  visit_postcode: "",
  visit_city: "",
  visit_province: "",
  visit_region: "",
  visit_country: "",

  // correspondentie
  corr_street: "",
  corr_house_number: "",
  corr_addition: "",
  corr_postcode: "",
  corr_city: "",
  corr_province: "",
  corr_region: "",
  corr_country: "",

  // flags
  correspondence_whatsapp: false,
  correspondence_email: false,
  invoice_digital: true,
  mailing_list: true,

  // bank/fin
  bank: "",
  iban: "",
  bic: "",
  account_name: "",
  g_iban: "",
  g_percentage: "",
  sales_factor: "",

  rate_junior: "",
  rate_junior_percent: "100.00",
  rate_medior: "",
  rate_medior_percent: "",
  rate_senior: "",
  rate_senior_percent: "",

  kvk: "",
  vat_number: "",
  vat_rate: "",

payment_terms: "",
payment_method: "",
payment_term_days: "",
credit_limit: "",

  remarks: "",

  active: true,
};

function emptyMatchingRow() {
  return {
    id: null,
    category: "",
    name: "",
    description: "",
    level: "",
    date_from: "",
    date_to: "",
    diploma: false,
    employee: "",
  };
}

function companyCodeLabel(row) {
  return safeTrim(row?.company_code_name || row?.company_code || row?.bedrijfscode || "");
}

function selectedCompanyCodeIdsFromScope() {
  const raw =
    sessionStorage.getItem("staffiq_company_code_ids") ||
    localStorage.getItem("staffiq_company_code_ids") ||
    "";

  return String(raw || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
}

function selectedCompanyCodesForForm(companyCodes = []) {
  const activeCodes = (companyCodes || []).filter((cc) => cc?.active !== false);
  const selectedIds = selectedCompanyCodeIdsFromScope();

  if (!selectedIds.length) return activeCodes;

  const selectedSet = new Set(selectedIds.map(String));
  const filtered = activeCodes.filter((cc) => selectedSet.has(String(cc.id)));

  return filtered.length ? filtered : activeCodes;
}

function firstCompanyCodeIdFromScope(companyCodes = []) {
  const selected = selectedCompanyCodesForForm(companyCodes);
  const first = selected[0];
  return first?.id ? String(first.id) : "";
}

// =====================================================
// SECTION 8: REACT COMPONENT

// Hoofdcomponent van de Organisaties-pagina.
// =====================================================
export default function Organisaties() {
  const rawUser =
    sessionStorage.getItem("user") ||
    localStorage.getItem("user") ||
    "";

let role = "user";

  try {
    role = JSON.parse(rawUser || "{}")?.role || "user";
  } catch {}

  const VERSION = "vORG-023";

  // ---------------------------------------------------
  // SUBSECTION 8.1: STATE
  // Lijstdata, formulierdata, filters, meldingen en UI.
  // ---------------------------------------------------

  const [items, setItems] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [companyCodes, setCompanyCodes] = useState([]);

  const [generalSettings, setGeneralSettings] = useState({
    use_organisations: true,
    use_particulieren: false,
  });

  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [matchingRows, setMatchingRows] = useState([]);
  const [matchingDraft, setMatchingDraft] = useState(emptyMatchingRow());
  const [matchingEditId, setMatchingEditId] = useState(null);
  const [matchingOpen, setMatchingOpen] = useState(false);

  const [touched, setTouched] = useState({
    search_name: false,
    account_name: false,
  });

  // ✅ nieuw: touched flags voor corr velden (om auto-kopie te stoppen zodra je zelf corr bewerkt)
  const [touchedCorr, setTouchedCorr] = useState({
    corr_street: false,
    corr_house_number: false,
    corr_addition: false,
    corr_postcode: false,
    corr_city: false,
    corr_province: false,
    corr_region: false,
    corr_country: false,
  });

  const [listIndex, setListIndex] = useState(-1);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [saveError, setSaveError] = useState(null);
  const [flash, setFlash] = useState("");

  const [reloadKey, setReloadKey] = useState(0);
  const [companyScopeTick, setCompanyScopeTick] = useState(0);
  const [speechInfo, setSpeechInfo] = useState("");

  const [hideVoiceError, setHideVoiceError] = useState(false);
  const prevVoiceErrorRef = useRef("");

  const [optionsTick, setOptionsTick] = useState(0);

  const [pendingSpeechSearch, setPendingSpeechSearch] = useState(false);

  const [q, setQ] = useState(getParam("q"));
  const [refreshScrollTick, setRefreshScrollTick] = useState(0);

  const EMPTY_VAL = "__EMPTY__";
  const [searchNameFilter, setSearchNameFilter] = useState("");
  const [entityFilter, setEntityFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [pcFilter, setPcFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [regionFilter, setRegionFilter] = useState(""); // ✅ nieuw
  const [phoneFilter, setPhoneFilter] = useState("");
  const [mobileFilter, setMobileFilter] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [adminFilter, setAdminFilter] = useState("");
  const [companyCodeFilter, setCompanyCodeFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState("j");

const [mode, setMode] = useState("search");

const isReadonlyRole = String(role || "").toLowerCase() === "readonly";
const isFormLocked = isReadonlyRole || mode !== "form";

// ✅ NIEUW: 1 centrale lock voor ALLES
const isFormDisabled = isReadonlyRole || isFormLocked;

  const [sort, setSort] = useState({ key: "default", dir: "asc" });

const [aiOpen, setAiOpen] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiText, setAiText] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function loadGeneralSettings() {
      try {
        const data = await tryFetchJson(["/api/general-settings"]);

        if (cancelled) return;

        setGeneralSettings({
          use_organisations: data?.use_organisations !== false,
          use_particulieren: data?.use_particulieren === true,
        });
      } catch {
        if (cancelled) return;

        setGeneralSettings({
          use_organisations: true,
          use_particulieren: false,
        });
      }
    }

    loadGeneralSettings();

    return () => {
      cancelled = true;
    };
  }, []);

  // ---------------------------------------------------
  // SUBSECTION 8.2: REFS
  // Navigatie, focusbeheer, remark-editor en scroll.
  // ---------------------------------------------------

  const pageTopRef = useRef(null);
  const searchRef = useRef(null);
  const rowRefs = useRef([]);
  const midScrollRef = useRef(null);
  const rightScrollRef = useRef(null);

  const entityTypeRef = useRef(null);
  const firstNameRef = useRef(null);
  const initialsRef = useRef(null);
  const namePrefixRef = useRef(null);
  const lastNameRef = useRef(null);
  const genderRef = useRef(null);
  const birthDateRef = useRef(null);
  const salutationRef = useRef(null);
  const attentionToRef = useRef(null);

  const nameRef = useRef(null);
  const searchNameRef = useRef(null);
  const parentRef = useRef(null);

  const typeRef = useRef(null);
  const adminRef = useRef(null);
  const firstContactRef = useRef(null);

  const phoneRef = useRef(null);
  const mobileRef = useRef(null);
  const emailRef = useRef(null);
  const webRef = useRef(null);

  const vStreetRef = useRef(null);
  const vHouseRef = useRef(null);
  const vAddRef = useRef(null);
  const vPcRef = useRef(null);
  const vCityRef = useRef(null);
  const vProvRef = useRef(null);
  const vRegionRef = useRef(null);
  const vCountryRef = useRef(null);

  const cStreetRef = useRef(null);
  const cHouseRef = useRef(null);
  const cAddRef = useRef(null);
  const cPcRef = useRef(null);
  const cCityRef = useRef(null);
  const cProvRef = useRef(null);
  const cRegionRef = useRef(null);
  const cCountryRef = useRef(null);

  const bankRef = useRef(null);
  const ibanRef = useRef(null);
  const bicRef = useRef(null);
  const accNameRef = useRef(null);
  const gIbanRef = useRef(null);
  const gPercRef = useRef(null);

  const rateJuniorRef = useRef(null);
  const rateJuniorPercentRef = useRef(null);
  const rateMediorRef = useRef(null);
  const rateMediorPercentRef = useRef(null);
  const rateSeniorRef = useRef(null);
  const rateSeniorPercentRef = useRef(null);

  const salesRef = useRef(null);

  const kvkRef = useRef(null);
  const vatNrRef = useRef(null);
  const vatRateRef = useRef(null);

  const payTermsRef = useRef(null);
  const payMethodRef = useRef(null);
  const payTermRef = useRef(null);
  const creditRef = useRef(null);

  const correspondenceWhatsappRef = useRef(null);
  const correspondenceEmailRef = useRef(null);
  const invoiceRef = useRef(null);
  const mailingRef = useRef(null);
  const activeRef = useRef(null);

  const remarkSmallRef = useRef(null);
  const remarkBigRef = useRef(null);
  const aiTextRef = useRef(null);

  const matchingSectionRef = useRef(null);
  const matchingFormRef = useRef(null);
  const matchingInputRef = useRef(null);

  const [remarkEditor, setRemarkEditor] = useState(false);
  const remarkDictateActiveRef = useRef(false);

  // ✅ voorkomt dat programmatic focus op klein Opmerking-veld meteen weer groot opent
  const ignoreNextRemarkSmallFocusRef = useRef(false);

  // ✅ behoud scrollpositie rechterkolom bij editor open/close
  function preserveRightScrollThen(fn) {
    const sc = rightScrollRef.current?.scrollTop ?? 0;
    fn();
    setTimeout(() => {
      try {
        if (rightScrollRef.current) rightScrollRef.current.scrollTop = sc;
      } catch {}
    }, 0);
  }

// ✅ FIX: nooit "keepScroll" gebruiken (dat is precies de reden dat hij naar boven springt)
  // Doel: altijd positioneren bij het doelveld in het rechterpaneel.


function closeRemarkEditorTo(ref, { scrollNearest = false } = {}) {
  const panel = rightScrollRef.current;
  const panelTop = panel ? panel.scrollTop : 0;

  // 1) editor uit, maar scrollpositie rechterpaneel bewaren
  setRemarkEditor(false);
  remarkDictateActiveRef.current = false;

  // 2) na render: focus zonder scroll + scrollpositie terugzetten
  requestAnimationFrame(() => {
    try {
      if (panel) panel.scrollTop = panelTop;
    } catch {}

    requestAnimationFrame(() => {
      try {
        const el = ref?.current;
        if (!el) return;

        // voorkom auto-open van groot als we programmatic naar klein gaan
        if (ref === remarkSmallRef) {
          ignoreNextRemarkSmallFocusRef.current = true;
        }

        // focus zonder scroll
        try {
          el.focus?.({ preventScroll: true });
        } catch {
          el.focus?.();
        }

        // zorg dat element zichtbaar is in rechterpaneel (zonder “naar boven knallen”)
        if (panel) {
          try {
            const top = el.offsetTop;
            const bottom = top + el.offsetHeight;
            const viewTop = panel.scrollTop;
            const viewBottom = viewTop + panel.clientHeight;
            const pad = 8;

            if (top < viewTop + pad) panel.scrollTop = Math.max(0, top - pad);
            else if (bottom > viewBottom - pad) panel.scrollTop = Math.max(0, bottom - panel.clientHeight + pad);
          } catch {}
        }

        // optioneel: alleen “nearest” (geen hard scrollen)
        if (scrollNearest) {
          try {
            el.scrollIntoView?.({ block: "nearest" });
          } catch {}
        }
      } catch {}
    });
  });
}

  // ✅ Open grote opmerking-editor en zet cursor op nieuwe regel aan het einde
  function openRemarkEditorAndFocusEnd() {
    preserveRightScrollThen(() => {
      // zorg dat er een newline klaarstaat (alleen als er al tekst is en hij niet al eindigt op newline)
      setForm((prev) => {
        const cur = String(prev.remarks || "");
        if (!cur) return prev;
        if (cur.endsWith("\n")) return prev;
        return { ...prev, remarks: cur + "\n" };
      });

      setRemarkEditor(true);
      // bij openen editor (via focus): niet meteen dicteren, pas bij "Spreek"
      remarkDictateActiveRef.current = false;
    });

    setTimeout(() => {
      try {
        const el = remarkBigRef.current;
        if (!el) return;
        // focus zonder scroll
        try {
          el.focus?.({ preventScroll: true });
        } catch {
          el.focus?.();
        }
        // cursor naar eind
        const len = String(el.value ?? "").length;
        el.setSelectionRange?.(len, len);
      } catch {}
    }, 0);
  }

  // ---------------------------------------------------
  // SUBSECTION 8.3: KEUZELIJSTEN EN AFGELEIDE OPTIES
  // Lokale en afgeleide opties voor selecties / filters.
  // ---------------------------------------------------
  const typeOptionsKey = "org_options_type";
  const adminOptionsKey = "org_options_admin_type";
  const bankOptionsKey = "org_options_bank";
  const vatRateOptionsKey = "org_options_vat_rate";
  const payTermsOptionsKey = "org_options_payment_terms";
  const payMethodOptionsKey = "org_options_payment_method";
  const payTermOptionsKey = "org_options_payment_term";

  const pcOptionsKey = "org_options_postcode";
  const cityOptionsKey = "org_options_city";
  const provOptionsKey = "org_options_province";
  const regionOptionsKey = "org_options_region";
  const countryOptionsKey = "org_options_country";

  const derivedType = useMemo(() => uniqSorted(items.map((x) => x.type || "")), [items]);
  const derivedAdmin = useMemo(() => uniqSorted(items.map((x) => x.administration_type || "")), [items]);
  const derivedBank = useMemo(() => uniqSorted(items.map((x) => x.bank || "")), [items]);
  const derivedVatRate = useMemo(() => uniqSorted(items.map((x) => x.vat_rate || "")), [items]);

  const derivedPayTerms = useMemo(() => uniqSorted(items.map((x) => x.payment_terms || "")), [items]);
  const derivedPayMethod = useMemo(() => uniqSorted(items.map((x) => x.payment_method || "")), [items]);
  const derivedPayTerm = useMemo(() => uniqSorted(items.map((x) => x.payment_term || "")), [items]);

  const derivedPC = useMemo(() => uniqSorted(items.map((x) => x.corr_postcode || "")), [items]);
  const derivedCity = useMemo(() => uniqSorted(items.map((x) => x.corr_city || "")), [items]);
  const derivedProv = useMemo(() => uniqSorted(items.map((x) => x.corr_province || "")), [items]);
  const derivedRegion = useMemo(() => uniqSorted(items.map((x) => x.corr_region || "")), [items]);
  const derivedCountry = useMemo(() => uniqSorted(items.map((x) => x.corr_country || "")), [items]);

  const typeOptions = useMemo(() => uniqSorted([...derivedType, ...loadLocalList(typeOptionsKey)]), [derivedType, optionsTick]);
  const adminTypeOptions = useMemo(() => uniqSorted([...derivedAdmin, ...loadLocalList(adminOptionsKey)]), [derivedAdmin, optionsTick]);
  const bankOptions = useMemo(() => uniqSorted([...derivedBank, ...loadLocalList(bankOptionsKey)]), [derivedBank, optionsTick]);
  const vatRateOptions = useMemo(() => uniqSorted([...derivedVatRate, ...loadLocalList(vatRateOptionsKey)]), [derivedVatRate, optionsTick]);

  const paymentTermsOptions = useMemo(() => uniqSorted([...derivedPayTerms, ...loadLocalList(payTermsOptionsKey)]), [derivedPayTerms, optionsTick]);
  const paymentMethodOptions = useMemo(() => uniqSorted([...derivedPayMethod, ...loadLocalList(payMethodOptionsKey)]), [derivedPayMethod, optionsTick]);
  const paymentTermOptions = useMemo(() => uniqSorted([...derivedPayTerm, ...loadLocalList(payTermOptionsKey)]), [derivedPayTerm, optionsTick]);

  const matchingCategoryOptions = useMemo(
    () =>
      uniqSorted([
        "Opleiding",
        "Werkervaring",
        "Certificaten",
        "Competenties",
        "Talenkennis",
        ...matchingRows.map((x) => x.category || ""),
      ]),
    [matchingRows]
  );

  const postcodeOptions = useMemo(() => uniqSorted([...derivedPC, ...loadLocalList(pcOptionsKey)]), [derivedPC, optionsTick]);
  const cityOptions = useMemo(() => uniqSorted([...derivedCity, ...loadLocalList(cityOptionsKey)]), [derivedCity, optionsTick]);
  const provinceOptions = useMemo(() => uniqSorted([...derivedProv, ...loadLocalList(provOptionsKey)]), [derivedProv, optionsTick]);
  const regionOptions = useMemo(() => uniqSorted([...derivedRegion, ...loadLocalList(regionOptionsKey)]), [derivedRegion, optionsTick]);
  const countryOptions = useMemo(() => uniqSorted([...derivedCountry, ...loadLocalList(countryOptionsKey)]), [derivedCountry, optionsTick]);

  // ---------------------------------------------------
  // SUBSECTION 8.4: FORM STATUS / AUTO-VOORSTELLEN
  // Bepaalt nieuw/bestaand en doet lichte normalisatie.
  // ---------------------------------------------------

function formHasId() {
    return form.id !== null && form.id !== undefined && String(form.id).length > 0;
  }

  const showEntityChoice =
    generalSettings.use_organisations === true &&
    generalSettings.use_particulieren === true;

  const defaultRelationEntityType =
    generalSettings.use_organisations === true
      ? "company"
      : "private";

  const currentRelationEntityType =
    form.relation_entity_type ||
    defaultRelationEntityType ||
    "company";

  const isPrivateRelation = currentRelationEntityType === "private";
  const isCompanyRelation = !isPrivateRelation;

  function relationEntityLabel(value) {
    const v = String(value || "company").toLowerCase();
    return v === "private" ? "P" : "O";
  }

function upperInitialsFromFirstName(firstName) {
    const t = safeTrim(firstName);
    if (!t) return "";

    return t
      .split(/\s+/)
      .filter(Boolean)
      .map((p) => (p[0] ? `${p[0].toUpperCase()}.` : ""))
      .join("");
  }

  function capitalizeFirstWordOnly(value) {
    const raw = safeTrim(value || "");
    if (!raw) return "";

    const parts = raw.split(/\s+/).filter(Boolean);
    if (!parts.length) return "";

    const first = parts[0];
    const fixedFirst = first.charAt(0).toUpperCase() + first.slice(1);

    return [fixedFirst, ...parts.slice(1)].join(" ");
  }

  function buildPrivateDisplayName(data) {
    const firstName = safeTrim(data.first_name || "");
    const initials = safeTrim(data.initials || "");
    const prefix = safeTrim(data.name_prefix || "");
    const lastName = safeTrim(data.last_name || "");

    const firstPart = firstName || initials;

    if (firstPart) {
      return joinSp([firstPart, prefix, lastName]);
    }

    return joinSp([capitalizeFirstWordOnly(prefix), lastName]);
  }

function buildPrivateSearchName(data) {
    const firstName = safeTrim(data.first_name || "");
    const initials = safeTrim(data.initials || "");
    const prefix = safeTrim(data.name_prefix || "");
    const lastName = safeTrim(data.last_name || "");

    const firstPart = firstName || initials;
    const afterComma = joinSp([firstPart, prefix]);

    if (lastName && afterComma) return `${lastName}, ${afterComma}`;
    if (lastName) return lastName;
    return afterComma || capitalizeFirstWordOnly(prefix);
  }

  function buildPrivateAccountName(data) {
    const initials = safeTrim(data.initials || "");
    const prefix = safeTrim(data.name_prefix || "");
    const lastName = safeTrim(data.last_name || "");

    if (initials) {
      return joinSp([initials, prefix, lastName]);
    }

    return joinSp([capitalizeFirstWordOnly(prefix), lastName]);
  }

// nieuw formulier: Datum 1e contact standaard vandaag
  useEffect(() => {
    if (selectedId !== null && selectedId !== undefined && String(selectedId) !== "") return;
    if (mode !== "form") return;
    if (safeTrim(form.first_contact_date)) return;

    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    const todayIso = `${yyyy}-${mm}-${dd}`;

    setForm((prev) => {
      if (safeTrim(prev.first_contact_date)) return prev;
      return { ...prev, first_contact_date: todayIso };
    });
  }, [selectedId, mode, form.first_contact_date]);

// auto voorstellen: zoeknaam + ten name van / particuliere naamopbouw
  useEffect(() => {
    setForm((prev) => {
      const next = { ...prev };
      let changed = false;

      const entityType = next.relation_entity_type || defaultRelationEntityType || "company";
      const isPrivate = entityType === "private";

      if (isPrivate) {
        if (!safeTrim(next.initials) && safeTrim(next.first_name)) {
          next.initials = upperInitialsFromFirstName(next.first_name);
          changed = true;
        }

        const privateName = buildPrivateDisplayName(next);
        const privateSearchName = buildPrivateSearchName(next);

        if (next.name !== privateName) {
          next.name = privateName;
          changed = true;
        }

        if (next.search_name !== privateSearchName) {
          next.search_name = privateSearchName;
          changed = true;
        }

const privateAccountName = buildPrivateAccountName(next);

        if (!touched.account_name && privateAccountName && next.account_name !== privateAccountName) {
          next.account_name = privateAccountName;
          changed = true;
        }
      } else {
        const nameFocused = (() => {
          try {
            return document.activeElement === nameRef.current;
          } catch {
            return false;
          }
        })();

        const nm = formatNameLike(next.name);

        if (!nameFocused && safeTrim(next.name) && next.name !== nm) {
          next.name = nm;
          changed = true;
        }

        if (!touched.search_name) {
          if (nm && next.search_name !== nm) {
            next.search_name = nm;
            changed = true;
          }
        }

        if (!touched.account_name) {
          if (nm && next.account_name !== nm) {
            next.account_name = nm;
            changed = true;
          }
        }
      }

if (next.phone) {
const p = next.phone.replace(/[^\d+\-\s]/g, "");
  if (p !== next.phone) {
    next.phone = p;
    changed = true;
  }
}

if (next.mobile) {
const p = next.mobile.replace(/[^\d+\-\s]/g, "");
  if (p !== next.mobile) {
    next.mobile = p;
    changed = true;
  }
}

      if (next.g_percentage !== "") {
        const v = intOrEmptyString(next.g_percentage);
        if (v !== String(next.g_percentage)) {
          next.g_percentage = v;
          changed = true;
        }
      }
      if (next.credit_limit !== "") {
        const v = intOrEmptyString(next.credit_limit);
        if (v !== String(next.credit_limit)) {
          next.credit_limit = v;
          changed = true;
        }
      }
      if (next.sales_factor !== "") {
        const v = decimal2OrEmptyString(next.sales_factor);
        if (v !== String(next.sales_factor)) {
          next.sales_factor = v;
          changed = true;
        }
      }

      if (next.visit_house_number) {
        const v = intOrEmptyString(next.visit_house_number);
        if (v !== next.visit_house_number) {
          next.visit_house_number = v;
          changed = true;
        }
      }
      if (next.corr_house_number) {
        const v = intOrEmptyString(next.corr_house_number);
        if (v !== next.corr_house_number) {
          next.corr_house_number = v;
          changed = true;
        }
      }

      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.relation_entity_type,
    form.first_name,
    form.initials,
    form.name_prefix,
    form.last_name,
    form.salutation,
    form.account_name,
    form.name,
    form.search_name,
    form.phone,
    form.mobile,
    form.g_percentage,
    form.sales_factor,
    form.credit_limit,
    form.visit_house_number,
    form.corr_house_number,
    touched.search_name,
    touched.account_name,
    defaultRelationEntityType,
  ]);

  // WhatsApp/E-mail-akkoord alleen toestaan als Mobiel/E-mail gevuld is.
  useEffect(() => {
    setForm((prev) => {
      const next = { ...prev };
      let changed = false;

      if (!safeTrim(next.mobile) && next.correspondence_whatsapp) {
        next.correspondence_whatsapp = false;
        changed = true;
      }

      if (!safeTrim(next.email) && next.correspondence_email) {
        next.correspondence_email = false;
        changed = true;
      }

      return changed ? next : prev;
    });
  }, [form.mobile, form.email, form.correspondence_whatsapp, form.correspondence_email]);

  // ✅ Fout b: bezoekadres -> correspondentieadres auto-kopie (nieuw vs bestaand)
  useEffect(() => {
    const isNew = !formHasId();

    setForm((prev) => {
      let changed = false;
      const next = { ...prev };

      const normPc = (v) => (v || "").toUpperCase().replace(/\s+/g, "");
      const normHouse = (v) => intOrEmptyString(v || "");

      const copyField = (visitKey, corrKey, transform = (x) => x) => {
        const v = transform(prev[visitKey] || "");
        const c = prev[corrKey] || "";

        // niets te kopiëren
        if (isBlank(v)) return;

        if (isNew) {
          // nieuw: altijd mee-kopiëren zolang corr-veld niet door gebruiker is aangeraakt
          if (!touchedCorr[corrKey] && c !== v) {
            next[corrKey] = v;
            changed = true;
          }
          return;
        }

        // bestaand: alleen kopiëren als corr-veld leeg is én niet net door user aangeraakt
        if (!touchedCorr[corrKey] && isBlank(c) && c !== v) {
          next[corrKey] = v;
          changed = true;
        }
      };

      copyField("visit_street", "corr_street");
      copyField("visit_house_number", "corr_house_number", normHouse);
      copyField("visit_addition", "corr_addition");
      copyField("visit_postcode", "corr_postcode");
      copyField("visit_city", "corr_city");
      copyField("visit_province", "corr_province");
      copyField("visit_region", "corr_region");
      copyField("visit_country", "corr_country");

      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.id,
    form.visit_street,
    form.visit_house_number,
    form.visit_addition,
    form.visit_postcode,
    form.visit_city,
    form.visit_province,
    form.visit_region,
    form.visit_country,
    touchedCorr,
  ]);

  // ---------------------------------------------------
  // SUBSECTION 8.5: DATA LADEN
  // Parent-organisaties en hoofdgrid.
  // ---------------------------------------------------

  // ---------- LOAD ORGS (voor parent dropdown) ----------
  useEffect(() => {
    let cancelled = false;
    async function loadOrgs() {
      try {
        const orgData = await tryFetchJson(["/api/organisations?q=", "/api/organisations"]);
        if (!cancelled) setOrgs(Array.isArray(orgData) ? orgData : []);
      } catch {
        if (!cancelled) setOrgs([]);
      }
    }
    loadOrgs();
    return () => (cancelled = true);
  }, []);

  // ---------- LOAD COMPANY CODES ----------
  useEffect(() => {
    let cancelled = false;

    async function loadCompanyCodes() {
      try {
        const data = await tryFetchJson(["/api/company-scope"]);
        const codes = Array.isArray(data?.company_codes) ? data.company_codes : [];
        if (!cancelled) setCompanyCodes(codes);
      } catch {
        if (!cancelled) setCompanyCodes([]);
      }
    }

    loadCompanyCodes();

    return () => {
      cancelled = true;
    };
  }, []);

  // ---------- RELOAD BIJ BEDRIJFSCODE-SCOPE ----------
  useEffect(() => {
    function onCompanyScopeChanged() {
      setCompanyScopeTick((k) => k + 1);
      setReloadKey((k) => k + 1);
    }

    window.addEventListener("staffiq-company-scope-changed", onCompanyScopeChanged);
    return () => {
      window.removeEventListener("staffiq-company-scope-changed", onCompanyScopeChanged);
    };
  }, []);

  // ---------- LOAD ITEMS ----------
const serverQuery = useMemo(() => {
  const p = new URLSearchParams();
  if (q) p.set("q", q);
  return p.toString();
}, [q]);

useEffect(() => {
  let cancelled = false;

  async function loadItems() {
    setLoading(true);
    setError(null);

try {
  const data = await tryFetchJson(["/api/organisations"]);
  if (cancelled) return;

  const arr = Array.isArray(data) ? data : [];
  const activeOnly = arr.filter((it) => activeToBool(it) === true);

  setItems(activeOnly);
} catch (e) {

  if (!cancelled) setError(e?.message || String(e));
} finally {
      if (!cancelled) setLoading(false);
    }
  }

  loadItems();

  return () => {
    cancelled = true;
  };
}, [reloadKey, companyScopeTick]);

  // ---------------------------------------------------
  // SUBSECTION 8.6: FILTEREN EN SORTEREN
  // Gridfilters, sortering en afgeleide lijsten.
  // ---------------------------------------------------

  // ---------- FILTER + SORT ----------
const filteredItems = useMemo(() => {
  const qq = norm(q);
  const arr = items.slice();

  const byQ = (it) => {
    if (!qq) return true;
    const hay = norm([it.search_name || "", it.name || "", it.type || "", it.email || ""].join(" "));
    return hay.includes(qq);
  };

const bySearchName = (it) => {
    if (!searchNameFilter) return true;
    const v = safeTrim(it.search_name || "");
    if (searchNameFilter === EMPTY_VAL) return !v;
    return v === searchNameFilter;
  };

  const byEntity = (it) => {
    if (!entityFilter) return true;
    const v = relationEntityLabel(it.relation_entity_type || "company");
    return v === entityFilter;
  };

  const byType = (it) => {
    if (!typeFilter) return true;
    const v = safeTrim(it.type || "");
    if (typeFilter === EMPTY_VAL) return !v;
    return v === typeFilter;
  };

  const byPC = (it) => {
    if (!pcFilter) return true;
    const v = safeTrim(it.corr_postcode || "");
    if (pcFilter === EMPTY_VAL) return !v;
    return v === pcFilter;
  };

  const byCity = (it) => {
    if (!cityFilter) return true;
    const v = safeTrim(it.corr_city || "");
    if (cityFilter === EMPTY_VAL) return !v;
    return v === cityFilter;
  };

  const byRegion = (it) => {
    if (!regionFilter) return true;
    const v = safeTrim(it.visit_region || "");
    if (regionFilter === EMPTY_VAL) return !v;
    return v === regionFilter;
  };

  const byPhone = (it) => {
    if (!phoneFilter) return true;
    const v = safeTrim(it.phone || "");
    if (phoneFilter === EMPTY_VAL) return !v;
    return v === phoneFilter;
  };

  const byMobile = (it) => {
    if (!mobileFilter) return true;
    const v = safeTrim(it.mobile || "");
    if (mobileFilter === EMPTY_VAL) return !v;
    return v === mobileFilter;
  };

  const byEmail = (it) => {
    if (!emailFilter) return true;
    const v = safeTrim(it.email || "");
    if (emailFilter === EMPTY_VAL) return !v;
    return v === emailFilter;
  };

  const byAdmin = (it) => {
    if (!adminFilter) return true;
    const v = safeTrim(it.administration_type || "");
    if (adminFilter === EMPTY_VAL) return !v;
    return v === adminFilter;
  };

  const byCompanyCode = (it) => {
    if (!companyCodeFilter) return true;
    const v = companyCodeLabel(it);
    if (companyCodeFilter === EMPTY_VAL) return !v;
    return v === companyCodeFilter;
  };

  const byActive = (it) => {
    if (!activeFilter) return true;
    const a = activeToBool(it);
    if (activeFilter === "j") return a === true;
    if (activeFilter === "n") return a === false;
    return true;
  };

return arr.filter((it) =>
    byQ(it) &&
    bySearchName(it) &&
    byEntity(it) &&
    byType(it) &&
    byPC(it) &&
    byCity(it) &&
    byRegion(it) &&
    byPhone(it) &&
    byMobile(it) &&
    byEmail(it) &&
    byAdmin(it) &&
    byCompanyCode(it) &&
    byActive(it)
  );
}, [
items,
  q,
  searchNameFilter,
  entityFilter,
  typeFilter,
  pcFilter,
  cityFilter,
  regionFilter, // ✅ dit zorgt dat Regio direct filtert
  phoneFilter,
  mobileFilter,
  emailFilter,
  adminFilter,
  companyCodeFilter,
  activeFilter,
]);

const sortedItems = useMemo(() => {
    const copy = filteredItems.slice();
    const dirMul = sort.dir === "desc" ? -1 : 1;

    const cmpText = (a, b) => {
      const aa = norm(a);
      const bb = norm(b);
      if (aa === bb) return 0;
      return aa < bb ? -1 : 1;
    };

    const defaultSort = (a, b) => cmpText(a.search_name || a.name || "", b.search_name || b.name || "");

    const customSort = (a, b) => {
      if (sort.key === "default") return defaultSort(a, b);
      if (sort.key === "search") return dirMul * cmpText(a.search_name || "", b.search_name || "");
      if (sort.key === "entity") return dirMul * cmpText(relationEntityLabel(a.relation_entity_type), relationEntityLabel(b.relation_entity_type));
      if (sort.key === "type") return dirMul * cmpText(a.type || "", b.type || "");
      if (sort.key === "pc") return dirMul * cmpText(a.corr_postcode || "", b.corr_postcode || "");
if (sort.key === "city") return dirMul * cmpText(a.corr_city || "", b.corr_city || "");
if (sort.key === "region") return dirMul * cmpText(a.visit_region || "", b.visit_region || ""); // ✅ nieuw
if (sort.key === "phone") return dirMul * cmpText(a.phone || "", b.phone || "");
if (sort.key === "mobile") return dirMul * cmpText(a.mobile || "", b.mobile || "");
      if (sort.key === "email") return dirMul * cmpText(a.email || "", b.email || "");
      if (sort.key === "admin") return dirMul * cmpText(a.administration_type || "", b.administration_type || "");
      if (sort.key === "company_code") return dirMul * cmpText(companyCodeLabel(a), companyCodeLabel(b));
      if (sort.key === "active") return dirMul * cmpText(activeToBool(a) ? "1" : "0", activeToBool(b) ? "1" : "0");
      return defaultSort(a, b);
    };

    copy.sort(customSort);
    return copy;
  }, [filteredItems, sort]);

  const gridCount = Math.min(sortedItems.length, 9999);

  const sortedMatchingRows = useMemo(() => {
    const copy = Array.isArray(matchingRows) ? matchingRows.slice() : [];

    copy.sort((a, b) => {
      const catA = norm(a.category || "");
      const catB = norm(b.category || "");

      if (catA !== catB) return catA < catB ? -1 : 1;

      const dateA = String(a.date_from || "").slice(0, 10);
      const dateB = String(b.date_from || "").slice(0, 10);

      const blankA = !dateA;
      const blankB = !dateB;

      if (blankA !== blankB) return blankA ? -1 : 1;
      if (dateA !== dateB) return dateA < dateB ? -1 : 1;

      return Number(a.id || 0) - Number(b.id || 0);
    });

    return copy;
  }, [matchingRows]);

  // ---------------------------------------------------
  // SUBSECTION 8.7: LIJSTNAVIGATIE EN FOCUS HELPERS
  // Cursor, selectie, scroll en focus-volgorde.
  // ---------------------------------------------------

  // ---------- LIST NAV HELPERS ----------
  function scrollMidTop() {
    try {
      if (midScrollRef.current) midScrollRef.current.scrollTop = 0;
    } catch {}
  }

  function scrollRightTop() {
    try {
      if (rightScrollRef.current) rightScrollRef.current.scrollTop = 0;
    } catch {}
  }

  function scrollRowIntoView(idx) {
    try {
      const el = rowRefs.current?.[idx];
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ block: "nearest" });
      }
    } catch {}
  }

  function clampIndex(idx, len) {
    const L = Number(len || 0);
    if (L <= 0) return -1;
    let x = Number.isFinite(idx) ? idx : 0;
    if (x < 0) x = 0;
    if (x > L - 1) x = L - 1;
    return x;
  }

  function ensureCursorExists() {
    const L = sortedItems.length;
    if (L <= 0) return -1;
    const idx = listIndex < 0 ? 0 : clampIndex(listIndex, L);
    if (idx !== listIndex) setListIndex(idx);
    return idx;
  }

  function moveCursor(delta) {
    setListIndex((prev) => {
      const L = sortedItems.length;
      if (L <= 0) return -1;
      const start = prev < 0 ? 0 : prev;
      const next = clampIndex(start + delta, L);
      setTimeout(() => scrollRowIntoView(next), 0);
      return next;
    });
  }

  // focus order rechts
  const focusOrderRefs = useMemo(
    () => [
      nameRef,
      searchNameRef,
      parentRef,

      typeRef,
      adminRef,
      firstContactRef,

      phoneRef,
      mobileRef,
      emailRef,
      webRef,

      vStreetRef,
      vHouseRef,
      vAddRef,
      vPcRef,
      vCityRef,
      vProvRef,
      vRegionRef,
      vCountryRef,

      cStreetRef,
      cHouseRef,
      cAddRef,
      cPcRef,
      cCityRef,
      cProvRef,
      cRegionRef,
      cCountryRef,

      bankRef,
      ibanRef,
      bicRef,
      accNameRef,
      gIbanRef,
      gPercRef,

      rateJuniorRef,
      rateJuniorPercentRef,
      rateMediorRef,
      rateMediorPercentRef,
      rateSeniorRef,
      rateSeniorPercentRef,

      salesRef,

      kvkRef,
      vatNrRef,
      vatRateRef,

      payTermsRef,
      payMethodRef,
      payTermRef,
      creditRef,

      correspondenceWhatsappRef,
      correspondenceEmailRef,
      invoiceRef,
      mailingRef,
      activeRef,

      // ✅ Opmerking: klein én groot in focus-volgorde
      // - in normale modus is remarkBigRef niet zichtbaar -> wordt gefilterd
      // - in remarkEditor is remarkSmallRef niet zichtbaar -> wordt gefilterd
      remarkSmallRef,
      remarkBigRef,
    ],
    []
  );

  function getFocusableElements() {
    const els = focusOrderRefs.map((r) => r?.current).filter(Boolean);
    return els.filter((el) => {
      try {
        if (el.disabled) return false;
        const rect = el.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0;
      } catch {
        return false;
      }
    });
  }

function focusFirstRightField() {
    setTimeout(() => {
      try {
        scrollRightTop();
        nameRef.current?.focus?.();
      } catch {}
    }, 0);
  }

function focusFirstRightFieldMobile() {
  setTimeout(() => {
    try {
      const panel = rightScrollRef.current;
      if (!panel) return;

      // eerst het rechter formulierpaneel intern naar boven
      try {
        panel.scrollTop = 0;
      } catch {}

      // daarna het hele formulierblok in beeld brengen
      panel.scrollIntoView?.({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
    } catch {}
  }, 120);
}

  function focusNextField(dir = +1) {
    const els = getFocusableElements();
    if (!els.length) return;

    const active = document.activeElement;
    let idx = els.findIndex((el) => el === active);
    if (idx === -1) idx = dir > 0 ? -1 : els.length;

    let next = idx + dir;
    if (next < 0) next = 0;
    if (next > els.length - 1) next = els.length - 1;

    const el = els[next];
    if (!el) return;

    try {
      el.focus?.();
      el.scrollIntoView?.({ block: "nearest" });
    } catch {}
  }

  function loadItemFromRowSync(it) {
    setSelectedId(it.id);

    const f = {
      ...emptyForm,
      id: it.id,

      relation_entity_type: it.relation_entity_type || "company",
      company_code_id: it.company_code_id ? String(it.company_code_id) : "",

      first_name: it.first_name || "",
      initials: it.initials || "",
      name_prefix: it.name_prefix || "",
      last_name: it.last_name || "",
      gender: it.gender || "",
      birth_date: (it.birth_date || "").toString().slice(0, 10),
      salutation: it.salutation || "",
      attention_to: it.attention_to || "",

      name: it.name || "",
      search_name: it.search_name || "",
      parent_organisation_id: it.parent_organisation_id ? String(it.parent_organisation_id) : "",

      type: it.type || "",
      administration_type: it.administration_type || "",
      first_contact_date: (it.first_contact_date || "").toString().slice(0, 10),

      phone: it.phone || "",
      mobile: it.mobile || "",
      email: it.email || "",
      website: it.website || "",

      visit_street: it.visit_street || "",
      visit_house_number: it.visit_house_number || "",
      visit_addition: it.visit_addition || "",
      visit_postcode: it.visit_postcode || "",
      visit_city: it.visit_city || "",
      visit_province: it.visit_province || "",
      visit_region: it.visit_region || "",
      visit_country: it.visit_country || "",

      corr_street: it.corr_street || "",
      corr_house_number: it.corr_house_number || "",
      corr_addition: it.corr_addition || "",
      corr_postcode: it.corr_postcode || "",
      corr_city: it.corr_city || "",
      corr_province: it.corr_province || "",
      corr_region: it.corr_region || "",
      corr_country: it.corr_country || "",

      correspondence_whatsapp: !!it.correspondence_whatsapp,
      correspondence_email: !!it.correspondence_email,
      invoice_digital: !!it.invoice_digital,
      mailing_list: !!it.mailing_list,

      bank: it.bank || "",
      iban: it.iban || "",
      bic: it.bic || "",
      account_name: it.account_name || "",
      g_iban: it.g_iban || "",
      g_percentage: it.g_percentage === null || it.g_percentage === undefined ? "" : String(it.g_percentage),
      sales_factor: it.sales_factor === null || it.sales_factor === undefined ? "" : String(it.sales_factor),

      rate_junior: it.rate_junior === null || it.rate_junior === undefined ? "" : String(it.rate_junior),
      rate_junior_percent: it.rate_junior_percent === null || it.rate_junior_percent === undefined ? "100.00" : String(it.rate_junior_percent),
      rate_medior: it.rate_medior === null || it.rate_medior === undefined ? "" : String(it.rate_medior),
      rate_medior_percent: it.rate_medior_percent === null || it.rate_medior_percent === undefined ? "" : String(it.rate_medior_percent),
      rate_senior: it.rate_senior === null || it.rate_senior === undefined ? "" : String(it.rate_senior),
      rate_senior_percent: it.rate_senior_percent === null || it.rate_senior_percent === undefined ? "" : String(it.rate_senior_percent),

      kvk: it.kvk || "",
      vat_number: it.vat_number || "",
      vat_rate: it.vat_rate || "",

payment_terms: it.payment_term || "",
payment_method: it.payment_method || "",
payment_term_days: it.payment_term_days === null || it.payment_term_days === undefined ? "" : String(it.payment_term_days),
credit_limit: it.credit_limit === null || it.credit_limit === undefined ? "" : String(it.credit_limit),

      remarks: it.remarks || "",

      active: activeToBool(it),
    };

    setForm(f);

    const nm = formatNameLike(f.name);
    setTouched({
      search_name: !!safeTrim(f.search_name) && norm(f.search_name) !== norm(nm),
      account_name: !!safeTrim(f.account_name) && norm(f.account_name) !== norm(nm),
    });

    // ✅ bij laden: corr is "niet-touched" (we gaan bij bestaand toch alleen vullen als leeg)
    setTouchedCorr({
      corr_street: false,
      corr_house_number: false,
      corr_addition: false,
      corr_postcode: false,
      corr_city: false,
      corr_province: false,
      corr_region: false,
      corr_country: false,
    });

    setSaveError(null);
    setFlash("");

    remarkDictateActiveRef.current = false;
    setRemarkEditor(false);
  }

  async function loadItemFromRow(it) {
    loadItemFromRowSync(it);

    try {
      const match = await tryFetchJson([
        `/api/organisations/${encodeURIComponent(it.id)}/matching`,
      ]);

      setMatchingRows(Array.isArray(match) ? match : []);
      setMatchingDraft(emptyMatchingRow());
      setMatchingEditId(null);
      setMatchingOpen(false);
    } catch {
      setMatchingRows([]);
      setMatchingDraft(emptyMatchingRow());
      setMatchingEditId(null);
      setMatchingOpen(false);
    }
  }

  function openCurrentRowToRight() {
    const L = sortedItems.length;
    if (L <= 0) return;

    const idx = ensureCursorExists();
    if (idx < 0) return;

    const it = sortedItems[idx];
    if (!it) return;

    setListIndex(idx);
    loadItemFromRow(it);
    setMode("form");
    setRemarkEditor(false);
    focusFirstRightField();
    setTimeout(() => scrollRowIntoView(idx), 0);
  }

  function highlightItemIndex(idx) {
    const L = sortedItems.length;
    if (L <= 0) return;
    const clamped = clampIndex(idx, L);
    setListIndex(clamped);
    setTimeout(() => scrollRowIntoView(clamped), 0);
  }

  useEffect(() => {
    if (!pendingSpeechSearch) return;
    if (loading) return;

    if (sortedItems.length > 0) {
      setListIndex(0);
      setTimeout(() => {
        scrollMidTop();
        scrollRowIntoView(0);
      }, 0);
    } else {
      setListIndex(-1);
      setTimeout(() => scrollMidTop(), 0);
    }
    setPendingSpeechSearch(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSpeechSearch, loading, sortedItems.length]);

  // ---------------------------------------------------
  // SUBSECTION 8.8: ACTIES / CRUD
  // Nieuw, vernieuwen, opslaan en verwijderen.
  // ---------------------------------------------------

  // ---------- ACTIES ----------
  function resetRightPanelEmpty() {
    setSelectedId(null);
    setForm({ ...emptyForm, active: true, company_code_id: firstCompanyCodeIdFromScope(companyCodes) });
    setMatchingRows([]);
    setMatchingDraft(emptyMatchingRow());
    setMatchingEditId(null);
    setMatchingOpen(false);
    setTouched({ search_name: false, account_name: false });
    setTouchedCorr({
      corr_street: false,
      corr_house_number: false,
      corr_addition: false,
      corr_postcode: false,
      corr_city: false,
      corr_province: false,
      corr_region: false,
      corr_country: false,
    });
    remarkDictateActiveRef.current = false;
    setRemarkEditor(false);
  }

function resetAllGridFilters() {
    setSearchNameFilter("");
    setEntityFilter("");
    setTypeFilter("");
setPcFilter("");
setCityFilter("");
setRegionFilter(""); // ✅ nieuw
setPhoneFilter("");
setMobileFilter("");
    setEmailFilter("");
    setAdminFilter("");
    setCompanyCodeFilter("");
    setActiveFilter("j");
  }



function forceRefreshPosition() {
  function resetScroll() {
    try {
      if (document.activeElement && typeof document.activeElement.blur === "function") {
        document.activeElement.blur();
      }
    } catch {}

    // Interne scrollposities altijd terugzetten.
    try {
      rightScrollRef.current?.scrollTo?.({ top: 0, left: 0, behavior: "auto" });
      if (rightScrollRef.current) {
        rightScrollRef.current.scrollTop = 0;
        rightScrollRef.current.scrollLeft = 0;
      }
    } catch {}

    try {
      midScrollRef.current?.scrollTo?.({ top: 0, left: 0, behavior: "auto" });
      if (midScrollRef.current) {
        midScrollRef.current.scrollTop = 0;
        midScrollRef.current.scrollLeft = 0;
      }
    } catch {}

    // Op mobiel moet Vernieuw terug naar het begin van het programma
    // (kop/zoekregel), niet naar de bovenkant van de invoerkolom.
    try {
      const topEl = pageTopRef.current || searchRef.current;
      if (topEl) {
        topEl.scrollIntoView?.({
          behavior: "auto",
          block: "start",
          inline: "nearest",
        });

        // Zet alle scrollbare ouders ook op 0, zoals nodig binnen App.js.
        let el = topEl.parentElement;
        let guard = 0;
        while (el && guard < 20) {
          try {
            if (typeof el.scrollTop === "number") el.scrollTop = 0;
            if (typeof el.scrollLeft === "number") el.scrollLeft = 0;
          } catch {}
          el = el.parentElement;
          guard += 1;
        }
      }
    } catch {}

    try {
      window.scrollTo?.({ top: 0, left: 0, behavior: "auto" });
    } catch {}

    try {
      document.documentElement.scrollTop = 0;
      document.documentElement.scrollLeft = 0;
      document.body.scrollTop = 0;
      document.body.scrollLeft = 0;
    } catch {}

    // Alleen op pc de cursor in Zoek zetten; op mobiel voorkomt dit dat
    // het toetsenbord/veld de pagina weer omlaag trekt.
    if (window.innerWidth > 768) {
      try {
        searchRef.current?.focus?.({ preventScroll: true });
        searchRef.current?.select?.();
      } catch {
        try {
          searchRef.current?.focus?.();
          searchRef.current?.select?.();
        } catch {}
      }
    }
  }

  resetScroll();

  requestAnimationFrame(() => {
    resetScroll();
    requestAnimationFrame(resetScroll);
  });

  [0, 50, 120, 250, 500, 900].forEach((ms) => {
    setTimeout(resetScroll, ms);
  });
}

function vernieuwList() {
  setQ("");
  resetAllGridFilters();
  resetRightPanelEmpty();
  setListIndex(-1);
  setSaveError(null);
  setFlash("Vernieuwd ✅");
  setHideVoiceError(true);
  setSort({ key: "default", dir: "asc" });
  setReloadKey((k) => k + 1);
  setMode("search");
  setRefreshScrollTick((k) => k + 1);

  forceRefreshPosition();

  setTimeout(() => setFlash(""), 800);
}

useEffect(() => {
  if (!refreshScrollTick) return;
  forceRefreshPosition();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [refreshScrollTick]);

  function afterSaveLikeRefresh() {
    setQ("");
    resetAllGridFilters();
    resetRightPanelEmpty();
    setListIndex(-1);
    setSaveError(null);
    setHideVoiceError(true);
    setSort({ key: "default", dir: "asc" });
    setReloadKey((k) => k + 1);
    setMode("search");

    // ✅ na opslaan: rechterkolom weer bovenaan
    setTimeout(() => {
      try {
        scrollRightTop();
      } catch {}
      try {
        searchRef.current?.focus?.();
      } catch {}
      try {
        scrollMidTop();
      } catch {}
    }, 0);
  }

function newItem() {
  setSelectedId(null);

  const nm = "";
  const today = new Date().toISOString().slice(0, 10);

const newForm = {
    ...emptyForm,
    relation_entity_type: defaultRelationEntityType,
    name: nm,
    search_name: nm,
    account_name: nm,
    first_contact_date: today,
    correspondence_whatsapp: false,
    correspondence_email: false,
    invoice_digital: true,
    mailing_list: true,
    active: true,
  };

    setForm(newForm);
    setTouched({ search_name: false, account_name: false });

    // ✅ bij nieuw: corr velden zijn nog niet "aangeraakt" -> auto-kopie mag
    setTouchedCorr({
      corr_street: false,
      corr_house_number: false,
      corr_addition: false,
      corr_postcode: false,
      corr_city: false,
      corr_province: false,
      corr_region: false,
      corr_country: false,
    });

setSaveError(null);
setFlash("");
setMode("form");
setListIndex(-1);
setHideVoiceError(true);

remarkDictateActiveRef.current = false;
setRemarkEditor(false);

if (window.innerWidth <= 768) {
  setTimeout(() => {
    try {
      const panel = rightScrollRef.current;
      if (!panel) return;

      panel.scrollTop = 0;
      panel.scrollIntoView?.({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
    } catch {}
  }, 120);
} else {
  focusFirstRightField();
}
  }

  async function saveItem() {
    if (isFormLocked) {
  alert("Alleen-lezen modus. Opslaan niet toegestaan.");
  return;
}
    setSaveError(null);
    setFlash("");

    ensureOption(typeOptionsKey, form.type);
    ensureOption(adminOptionsKey, form.administration_type);
    ensureOption(bankOptionsKey, form.bank);
    ensureOption(vatRateOptionsKey, form.vat_rate);
    ensureOption(payTermsOptionsKey, form.payment_terms);
    ensureOption(payMethodOptionsKey, form.payment_method);
    ensureOption(payTermOptionsKey, form.payment_term);

    ensureOption(pcOptionsKey, form.corr_postcode);
    ensureOption(cityOptionsKey, form.corr_city);
    ensureOption(provOptionsKey, form.corr_province);
    ensureOption(regionOptionsKey, form.corr_region);
    ensureOption(countryOptionsKey, form.corr_country);

    ensureOption(pcOptionsKey, form.visit_postcode);
    ensureOption(cityOptionsKey, form.visit_city);
    ensureOption(provOptionsKey, form.visit_province);
    ensureOption(regionOptionsKey, form.visit_region);
    ensureOption(countryOptionsKey, form.visit_country);

    setOptionsTick((x) => x + 1);

const payload = {
      relation_entity_type: form.relation_entity_type || defaultRelationEntityType || "company",
      company_code_id: form.company_code_id ? Number(form.company_code_id) : null,

      first_name: form.first_name || "",
      initials: form.initials || "",
      name_prefix: form.name_prefix || "",
      last_name: form.last_name || "",
      gender: form.gender || "",
      birth_date: form.birth_date || null,
      salutation: form.salutation || "",
      attention_to: form.attention_to || "",

      name: formatNameLike(form.name || ""),
      search_name: form.search_name || "",
      parent_organisation_id:
        (form.relation_entity_type || defaultRelationEntityType) === "private"
          ? null
          : form.parent_organisation_id
            ? Number(form.parent_organisation_id)
            : null,

      type: form.type || "",
      administration_type: form.administration_type || "",
      first_contact_date: form.first_contact_date || null,

phone: form.phone,
mobile: form.mobile,
      email: form.email || "",
      website: form.website || "",

      visit_street: form.visit_street || "",
      visit_house_number: intOrEmptyString(form.visit_house_number || ""),
      visit_addition: form.visit_addition || "",
      visit_postcode: (form.visit_postcode || "").toUpperCase().replace(/\s+/g, ""),
      visit_city: form.visit_city || "",
      visit_province: form.visit_province || "",
      visit_region: form.visit_region || "",
      visit_country: form.visit_country || "",

      corr_street: form.corr_street || "",
      corr_house_number: intOrEmptyString(form.corr_house_number || ""),
      corr_addition: form.corr_addition || "",
      corr_postcode: (form.corr_postcode || "").toUpperCase().replace(/\s+/g, ""),
      corr_city: form.corr_city || "",
      corr_province: form.corr_province || "",
      corr_region: form.corr_region || "",
      corr_country: form.corr_country || "",

      correspondence_whatsapp: safeTrim(form.mobile) ? !!form.correspondence_whatsapp : false,
      correspondence_email: safeTrim(form.email) ? !!form.correspondence_email : false,
      invoice_digital: !!form.invoice_digital,
      mailing_list: !!form.mailing_list,

      bank: form.bank || "",
      iban: form.iban || "",
      bic: form.bic || "",
      account_name: form.account_name || "",
      g_iban: form.g_iban || "",
      g_percentage: form.g_percentage === "" ? null : Number(intOrEmptyString(form.g_percentage)),
      sales_factor: form.sales_factor === "" ? null : Number(decimal2OrEmptyString(form.sales_factor)),

      rate_junior: form.rate_junior === "" ? null : Number(decimal2OrEmptyString(form.rate_junior)),
      rate_junior_percent: form.rate_junior_percent === "" ? null : Number(decimalPercentOrEmptyString(form.rate_junior_percent)),
      rate_medior: form.rate_medior === "" ? null : Number(decimal2OrEmptyString(form.rate_medior)),
      rate_medior_percent: form.rate_medior_percent === "" ? null : Number(decimalPercentOrEmptyString(form.rate_medior_percent)),
      rate_senior: form.rate_senior === "" ? null : Number(decimal2OrEmptyString(form.rate_senior)),
      rate_senior_percent: form.rate_senior_percent === "" ? null : Number(decimalPercentOrEmptyString(form.rate_senior_percent)),

      kvk: form.kvk || "",
      vat_number: form.vat_number || "",
      vat_rate: form.vat_rate || "",

      payment_terms: form.payment_terms || "",
      payment_method: form.payment_method || "",
      payment_term_days: Number(form.payment_term_days || 0),
      credit_limit: form.credit_limit === "" ? null : Number(intOrEmptyString(form.credit_limit)),

      remarks: form.remarks || "",

      active: !!form.active,
      is_active: !!form.active,
      actief: !!form.active,
    };

    const isUpdate = formHasId();
    const method = isUpdate ? "PUT" : "POST";
    const urls = isUpdate ? [`/api/organisations/${form.id}`] : ["/api/organisations"];

    try {
      const saved = await tryFetchJson(urls, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (saved && typeof saved === "object") {
        setItems((prev) => {
          const id = saved?.id;
          if (id === null || id === undefined) return prev;
          const idx = prev.findIndex((x) => String(x.id) === String(id));
          if (idx === -1) return [saved, ...prev];
          const copy = prev.slice();
          copy[idx] = { ...copy[idx], ...saved };
          return copy;
        });
      }

      setFlash("Opgeslagen ✅");
      setTimeout(() => setFlash(""), 800);

      afterSaveLikeRefresh();
    } catch (e) {
      setSaveError(e?.message || String(e));
    }
  }

  async function deleteItem() {
    if (isFormLocked) {
  alert("Alleen-lezen modus. Verwijderen niet toegestaan.");
  return;
}
    setSaveError(null);
    setFlash("");

    if (!formHasId()) return;
    const ok = window.confirm("Weet je zeker dat je deze organisatie wilt verwijderen?");
    if (!ok) return;

    const id = form.id;
    try {
      await tryFetchJson([`/api/organisations/${id}`], { method: "DELETE" });

      setItems((prev) => prev.filter((x) => String(x.id) !== String(id)));
      resetRightPanelEmpty();
      setListIndex(-1);

      setFlash("Verwijderd ✅");
      setTimeout(() => setFlash(""), 800);

      setMode("search");
      setTimeout(() => searchRef.current?.focus?.(), 0);
      setTimeout(() => scrollMidTop(), 0);
    } catch (e) {
      setSaveError(e?.message || String(e));
    }
  }

  function positionMatchingBlockNoKeyboard() {
    setTimeout(() => {
      try {
        if (document.activeElement && typeof document.activeElement.blur === "function") {
          document.activeElement.blur();
        }
      } catch {}

      try {
        matchingSectionRef.current?.scrollIntoView?.({
          behavior: "smooth",
          block: "start",
          inline: "nearest",
        });
      } catch {}
    }, 80);
  }

  function positionMatchingFormAndFocusCategory() {
    setTimeout(() => {
      try {
        matchingFormRef.current?.scrollIntoView?.({
          behavior: "smooth",
          block: "start",
          inline: "nearest",
        });
      } catch {}

      setTimeout(() => {
        try {
          matchingInputRef.current?.focus?.();
          matchingInputRef.current?.select?.();
        } catch {}
      }, 220);
    }, 120);
  }

  function positionMatchingHeaderNoKeyboard() {
    setTimeout(() => {
      try {
        if (document.activeElement && typeof document.activeElement.blur === "function") {
          document.activeElement.blur();
        }
      } catch {}

      try {
        matchingSectionRef.current?.scrollIntoView?.({
          behavior: "smooth",
          block: "start",
          inline: "nearest",
        });
      } catch {}
    }, 120);
  }

  function openMatchingBlock() {
    if (!formHasId()) {
      setSaveError("Sla de relatie eerst op voordat je matchinggegevens toevoegt.");
      return;
    }

    setSaveError("");
    setMatchingOpen(true);
    positionMatchingBlockNoKeyboard();
  }

  function closeMatchingBlock() {
    setMatchingEditId(null);
    setMatchingDraft(emptyMatchingRow());
    setMatchingOpen(false);
    scrollRightTop();
  }

  function startEditMatchingRow(row) {
    if (!row) return;

    setMatchingOpen(true);
    setMatchingEditId(row.id || null);
    setMatchingDraft({
      id: row.id || null,
      category: row.category || "",
      name: row.name || "",
      description: row.description || "",
      level: row.level || "",
      date_from: String(row.date_from || "").slice(0, 10),
      date_to: String(row.date_to || "").slice(0, 10),
      diploma: row.diploma === true,
      employee: row.employee || "",
    });

    positionMatchingFormAndFocusCategory();
  }

  function cancelEditMatchingRow() {
    setMatchingEditId(null);
    setMatchingDraft(emptyMatchingRow());
    scrollRightTop();
  }

  async function saveMatchingRow() {
    if (!formHasId()) {
      setSaveError("Sla de relatie eerst op voordat je matchinggegevens toevoegt.");
      return;
    }

    const cleanDraft = {
      ...matchingDraft,
      category: safeTrim(matchingDraft.category),
      name: safeTrim(matchingDraft.name),
      description: safeTrim(matchingDraft.description),
      level: safeTrim(matchingDraft.level),
      employee: safeTrim(matchingDraft.employee),
      date_from: matchingDraft.date_from || "",
      date_to: matchingDraft.date_to || "",
      diploma: matchingDraft.diploma === true,
    };

    const hasAnyMatchingValue =
      safeTrim(cleanDraft.category) ||
      safeTrim(cleanDraft.name) ||
      safeTrim(cleanDraft.description) ||
      safeTrim(cleanDraft.level) ||
      safeTrim(cleanDraft.employee) ||
      safeTrim(cleanDraft.date_from) ||
      safeTrim(cleanDraft.date_to) ||
      cleanDraft.diploma === true;

    if (!hasAnyMatchingValue) {
      setSaveError("Vul minimaal één veld in bij Matching gegevens.");
      return;
    }

    if (!cleanDraft.category) {
      cleanDraft.category = "Algemeen";
    }

    try {
      setSaveError("");

      const isEdit = !!matchingEditId;

      const saved = await tryFetchJson(
        isEdit
          ? [`/api/organisation-matching/${matchingEditId}`]
          : [`/api/organisations/${form.id}/matching`],
        {
          method: isEdit ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(cleanDraft),
        }
      );

      if (isEdit) {
        setMatchingRows((prev) =>
          prev.map((x) =>
            String(x.id) === String(matchingEditId)
              ? { ...x, ...saved }
              : x
          )
        );
      } else {
        setMatchingRows((prev) => [...prev, saved]);
      }

      setMatchingEditId(null);
      setMatchingDraft(emptyMatchingRow());
      setMatchingOpen(true);

      setFlash(isEdit ? "Matchingregel gewijzigd ✅" : "Matchingregel toegevoegd ✅");
      setTimeout(() => setFlash(""), 900);

      positionMatchingBlockNoKeyboard();
    } catch (e) {
      setSaveError(e?.message || String(e));
    }
  }

  async function deleteMatchingRow(id) {
    if (!id) return;
    if (!window.confirm("Matchingregel verwijderen?")) return;

    try {
      await tryFetchJson([`/api/organisation-matching/${id}`], { method: "DELETE" });
      setMatchingRows((prev) => prev.filter((x) => String(x.id) !== String(id)));

      if (String(matchingEditId || "") === String(id)) {
        setMatchingEditId(null);
        setMatchingDraft(emptyMatchingRow());
      }

      positionMatchingHeaderNoKeyboard();
    } catch (e) {
      setSaveError(e?.message || String(e));
    }
  }

  // ---------------------------------------------------
  // SUBSECTION 8.9: SPRAAKCONTEXT EN DICTEER-HELPERS
  // Contextbepaling, voice-commando's en veldinvoer.
  // ---------------------------------------------------

  // ---------- SPEECH: context + dictation overwrite ----------
  function inFormContext() {
    if (remarkEditor) return true;
    if (mode === "form") return true;
    try {
      const el = document.activeElement;
      if (!el) return false;
      return rightScrollRef.current?.contains?.(el) || false;
    } catch {
      return false;
    }
  }

  function focusElEnd(ref) {
    setTimeout(() => {
      try {
        const el = ref?.current;
        if (!el) return;

        try {
          el.focus?.({ preventScroll: true });
        } catch {
          el.focus?.();
        }

        const len = String(el.value ?? "").length;
        if (typeof el.setSelectionRange === "function") {
          el.setSelectionRange(len, len);
        }
      } catch {}
    }, 0);
  }

  function preserveScrollThen(fnAfterStateChange) {
    const winX = window.scrollX || 0;
    const winY = window.scrollY || 0;

    const panel = rightScrollRef.current;
    const panelTop = panel ? panel.scrollTop : 0;

    fnAfterStateChange?.();

    requestAnimationFrame(() => {
      try {
        window.scrollTo(winX, winY);
      } catch {}
      try {
        if (panel) panel.scrollTop = panelTop;
      } catch {}

      requestAnimationFrame(() => {
        try {
          window.scrollTo(winX, winY);
        } catch {}
        try {
          if (panel) panel.scrollTop = panelTop;
        } catch {}
      });
    });
  }

  function focusNoScroll(ref) {
    setTimeout(() => {
      try {
        const el = ref?.current;
        if (!el) return;

        try {
          el.focus?.({ preventScroll: true });
        } catch {
          el.focus?.();
        }

        const len = String(el.value ?? "").length;
        el.setSelectionRange?.(len, len);
      } catch {}
    }, 0);
  }

  function handleVoiceCommandLike(raw) {
    const t = norm(raw);
    const parts = t.split(" ").filter(Boolean);
    const w1 = parts[0] || "";

    if (w1 === "volgende") {
      if (inFormContext()) {
        // ✅ speciaal: vanaf Kredietlimiet direct naar Opmerking (groot) + cursor op einde
        try {
          if (document.activeElement === creditRef.current) {
            openRemarkEditorAndFocusEnd();
            return true;
          }
        } catch {}

        focusNextField(+1);
      } else {
        moveCursor(+1);
      }
      return true;
    }

if (w1 === "vorige") {
  // ✅ In grote Opmerking-editor: EXACT hetzelfde als "Terug" knop
  if (remarkEditor) {
    closeRemarkEditorTo(remarkSmallRef);
    return true;
  }
  if (inFormContext()) focusNextField(-1);
  else moveCursor(-1);
  return true;
}

    if (w1 === "terug" || w1 === "links") {
      // ✅ Als we in grote Opmerking-editor zitten: maak klein en blijf op Opmerking (klein)
      if (remarkEditor) {
        closeRemarkEditorTo(remarkSmallRef); // exact als knop Terug (positioneren bij klein veld)
        return true;
      }

      // ✅ Anders: terug naar links/zoek
      resetRightPanelEmpty();
      setMode("search");
      setRemarkEditor(false);

      setTimeout(() => {
        try {
          scrollMidTop();
        } catch {}
        try {
          searchRef.current?.focus?.({ preventScroll: true });
        } catch {
          searchRef.current?.focus?.();
        }
      }, 0);

      return true;
    }

    if (w1 === "rechts" || w1 === "selecteer" || w1 === "selecteren") {
      openCurrentRowToRight();
      return true;
    }

    if (w1 === "item") {
      const n = parseInt(parts?.[1] || "", 10);
      if (Number.isFinite(n) && n > 0) {
        highlightItemIndex(n - 1);
        if (parts.includes("rechts") || parts.includes("selecteer") || parts.includes("selecteren")) {
          setTimeout(() => openCurrentRowToRight(), 0);
        }
        return true;
      }
      openCurrentRowToRight();
      return true;
    }

    if (w1 === "nieuw" || w1 === "nieuwe") {
  newItem();
  return true;
}

if (w1 === "vernieuw" || w1 === "vernieuwen") {
  vernieuwList();
  return true;
}

if (w1 === "opslaan" || w1 === "bewaren") {
  saveItem();
  return true;
}
   if (w1 === "verwijder" || w1 === "verwijderen" || w1 === "delete") {
  deleteItem();
  return true;
}

    return false;
  }

  function applyDictationToFocusedField(raw0) {
    const el = document.activeElement;
    if (!el) return false;

    if (el === remarkSmallRef.current) return false;

    const tag = (el.tagName || "").toLowerCase();
    const isInput = tag === "input";
    const isTextarea = tag === "textarea";
    const isSelect = tag === "select";
    if (!isInput && !isTextarea && !isSelect) return false;

    if (isInput && el.type === "checkbox") {
      const s = norm(raw0);
      if (["ja", "j", "aan", "on", "true", "1"].includes(s)) el.click();
      else if (["nee", "n", "uit", "off", "false", "0"].includes(s)) el.click();
      else el.click();
      return true;
    }

    const punct = punctToChar(raw0);
    const spoken = punct !== null ? punct : raw0;

    let v = spoken;

    if (el === nameRef.current) v = formatNameLike(v);
if (el === phoneRef.current || el === mobileRef.current) {
  v = (v || "").replace(/[^\d+\-\s]/g, "");
}

    if (el === vHouseRef.current || el === cHouseRef.current) v = intOrEmptyString(v);
    if (el === gPercRef.current || el === creditRef.current) v = intOrEmptyString(v);
    if (el === salesRef.current) v = (v || "").replace(",", ".");

    if (el === firstContactRef.current || (isInput && el.type === "date")) {
      const iso = parseDutchDateToISO(v);
      if (iso) v = iso;
    }

    const setByKey = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

    const refMap = new Map([
      [nameRef.current, "name"],
      [searchNameRef.current, "search_name"],
      [parentRef.current, "parent_organisation_id"],

      [typeRef.current, "type"],
      [adminRef.current, "administration_type"],
      [firstContactRef.current, "first_contact_date"],

      [phoneRef.current, "phone"],
      [mobileRef.current, "mobile"],
      [emailRef.current, "email"],
      [webRef.current, "website"],

      [vStreetRef.current, "visit_street"],
      [vHouseRef.current, "visit_house_number"],
      [vAddRef.current, "visit_addition"],
      [vPcRef.current, "visit_postcode"],
      [vCityRef.current, "visit_city"],
      [vProvRef.current, "visit_province"],
      [vRegionRef.current, "visit_region"],
      [vCountryRef.current, "visit_country"],

      [cStreetRef.current, "corr_street"],
      [cHouseRef.current, "corr_house_number"],
      [cAddRef.current, "corr_addition"],
      [cPcRef.current, "corr_postcode"],
      [cCityRef.current, "corr_city"],
      [cProvRef.current, "corr_province"],
      [cRegionRef.current, "corr_region"],
      [cCountryRef.current, "corr_country"],

      [bankRef.current, "bank"],
      [ibanRef.current, "iban"],
      [bicRef.current, "bic"],
      [accNameRef.current, "account_name"],
      [gIbanRef.current, "g_iban"],
      [gPercRef.current, "g_percentage"],
      [salesRef.current, "sales_factor"],

      [kvkRef.current, "kvk"],
      [vatNrRef.current, "vat_number"],
      [vatRateRef.current, "vat_rate"],

      [payTermsRef.current, "payment_terms"],
      [payMethodRef.current, "payment_method"],
      [payTermRef.current, "payment_term"],
      [creditRef.current, "credit_limit"],
    ]);

    const key = refMap.get(el);
    if (!key) return false;

    if (key === "search_name") setTouched((x) => ({ ...x, search_name: true }));
    if (key === "account_name") setTouched((x) => ({ ...x, account_name: true }));

    // ✅ corr touched bij inspraak
    if (key.startsWith("corr_")) setTouchedCorr((x) => ({ ...x, [key]: true }));

    setByKey(key, v);
    return true;
  }

  // ---------------------------------------------------
  // SUBSECTION 8.10: USEVOICECOMMANDS KOPPELING
  // Algemene commando's en vrije dictatie.
  // ---------------------------------------------------

  // ---------- SPEECH ----------
  const voice = useVoiceCommands({
    lang: "nl-NL",
    timeoutMs: 6500,

    onCommand: (cmd, rawHeard) => {
      const raw = String(rawHeard || "").trim();

      if (raw) {
        setSpeechInfo(`🎙 ${raw}`);
        setTimeout(() => setSpeechInfo(""), 2000);
      }

      if (raw && handleVoiceCommandLike(raw)) return true;

      if (!cmd || !cmd.type) return false;

      if (cmd.type === "new" || cmd.type === "nieuw") return newItem(), true;
      if (cmd.type === "refresh") return vernieuwList(), true;
      if (cmd.type === "save") return saveItem(), true;
      if (cmd.type === "delete") return deleteItem(), true;

      return false;
    },

onDictation: (text) => {
  const raw = String(text || "").trim();
  if (!raw) return;

  setSpeechInfo(`🎤 ${raw}`);
  setTimeout(() => setSpeechInfo(""), 2000);

if (aiOpen) {
    setAiText((prev) => {
      const cur = String(prev || "");
      const sep = cur && !cur.endsWith(" ") && !cur.endsWith("\n") ? " " : "";

      let txt = raw
        .replace(/\bnieuwe regel\b/gi, "\n")
        .replace(/\bpuntkomma\b/gi, ";")
        .replace(/\bdubbele punt\b/gi, ":")
        .replace(/\buitroepteken\b/gi, "!")
        .replace(/\bvraagteken\b/gi, "?")
        .replace(/\bkomma\b/gi, ",")
        .replace(/\bpunt\b/gi, ".")
        .replace(/\s+([,.;:!?])/g, "$1");

      txt = txt.replace(/(^|[\n.!?]\s*)([a-zà-ÿ])/g, (_, p1, p2) => p1 + p2.toUpperCase());

      return cur + sep + txt;
    });
    return;
  }

  if (remarkDictateActiveRef.current) {
    setForm((prev) => {
      const cur = String(prev.remarks || "");
      const sep = cur && !cur.endsWith(" ") && !cur.endsWith("\n") ? " " : "";

      let txt = raw
        .replace(/\bnieuwe regel\b/gi, "\n")
        .replace(/\bpuntkomma\b/gi, ";")
        .replace(/\bdubbele punt\b/gi, ":")
        .replace(/\buitroepteken\b/gi, "!")
        .replace(/\bvraagteken\b/gi, "?")
        .replace(/\bkomma\b/gi, ",")
        .replace(/\bpunt\b/gi, ".")
        .replace(/\s+([.,!?;:])/g, "$1");

      txt = txt.replace(/(^|[\n.!?]\s*)([a-zà-ÿ])/g, (_, p1, p2) => p1 + p2.toUpperCase());

      return { ...prev, remarks: cur + sep + txt };
    });
    return;
  }

  if (handleVoiceCommandLike(raw)) return;

  if (inFormContext()) {
    if (applyDictationToFocusedField(raw)) return;
  }

  if (mode === "search") {
    if (isEmptyWord(raw)) {
      setQ("");
      setPendingSpeechSearch(true);
    } else {
      setQ(raw);
      setPendingSpeechSearch(true);
    }
  }
},

  });

  useEffect(() => {
    const cur = String(voice.error || "");
    const prev = String(prevVoiceErrorRef.current || "");
    if (cur && cur !== prev) {
      setHideVoiceError(false);
      prevVoiceErrorRef.current = cur;
    }
    if (!cur) prevVoiceErrorRef.current = "";
  }, [voice.error]);

  function startSpeakSmart() {
    if (!voice.supported || voice.listening) return;

    const inRemark = mode === "form" && remarkEditor;

    if (inRemark) {
      // ✅ in opmerking-editor: door-dicteren + cursor op nieuwe regel aan einde
      remarkDictateActiveRef.current = true;

      setTimeout(() => {
        try {
          const el = remarkBigRef.current;
          if (!el) return;

          const cur = String(el.value || "");
          const nextVal = cur && !cur.endsWith("\n") ? cur + "\n" : cur;

          if (nextVal !== cur) {
            setForm((prev) => ({ ...prev, remarks: nextVal }));
            setTimeout(() => {
              try {
                const el2 = remarkBigRef.current;
                if (!el2) return;
                const p = String(el2.value || "").length;
                el2.focus();
                el2.setSelectionRange(p, p);
              } catch {}
            }, 0);
          } else {
            const p = nextVal.length;
            el.focus();
            el.setSelectionRange(p, p);
          }
        } catch {}
      }, 0);
    } else {
      remarkDictateActiveRef.current = false;
    }

    voice.startOnce();
  }

  // ---------------------------------------------------
  // SUBSECTION 8.11: LAYOUT EN STYLING
  // Constantes en style-helpers voor de pagina.
  // ---------------------------------------------------

// ---------- LAYOUT ----------
  function setRateAmount(level, value) {
    const amount = decimal2OrEmptyString(value);

    setForm((prev) => {
      const next = { ...prev };
      const key = `rate_${level}`;
      const percentKey = `rate_${level}_percent`;

      next[key] = amount;

      const junior = level === "junior"
        ? parseDecimalNumber(amount)
        : parseDecimalNumber(next.rate_junior);

      if (level === "junior") {
        next.rate_junior_percent = "100.00";

        const medior = parseDecimalNumber(next.rate_medior);
        const senior = parseDecimalNumber(next.rate_senior);

        if (junior && medior !== null) {
          next.rate_medior_percent = formatDecimalForStorage((medior / junior) * 100, 2);
        }

        if (junior && senior !== null) {
          next.rate_senior_percent = formatDecimalForStorage((senior / junior) * 100, 2);
        }
      } else {
        const current = parseDecimalNumber(amount);

        if (junior && current !== null) {
          next[percentKey] = formatDecimalForStorage((current / junior) * 100, 2);
        }
      }

      return next;
    });
  }

  function setRatePercent(level, value) {
    const percent = level === "junior" ? "100.00" : decimalPercentOrEmptyString(value);

    setForm((prev) => {
      const next = { ...prev };
      const percentKey = `rate_${level}_percent`;
      const amountKey = `rate_${level}`;

      next[percentKey] = percent;

      const junior = parseDecimalNumber(next.rate_junior);
      const pct = parseDecimalNumber(percent);

      if (level !== "junior" && junior && pct !== null) {
        next[amountKey] = formatDecimalForStorage((junior * pct) / 100, 2);
      }

      if (level === "junior") {
        next.rate_junior_percent = "100.00";
      }

      return next;
    });
  }

  function blurRateAmount(level) {
    const key = `rate_${level}`;
    setForm((prev) => ({
      ...prev,
      [key]: formatDecimalForStorage(prev[key], 2),
    }));
  }

  function blurRatePercent(level) {
    const key = `rate_${level}_percent`;
    setForm((prev) => ({
      ...prev,
      [key]: level === "junior" ? "100.00" : formatDecimalForStorage(prev[key], 2),
    }));
  }
  const isMobile = window.innerWidth <= 768;
  const RIGHT_WIDTH = isMobile ? "100%" : 320;
  const GAP = isMobile ? 18 : 14;
  const TABLE_MIN_WIDTH = showEntityChoice ? 1110 : 1070;
  const MID_BOTTOM_OFFSET = isMobile ? 90 : 135;
  const colWidths = {
  search: 90,
  entity: 50,
  type: 60,
  pc: 60,
  city: 80,
  region: 60, // ✅ nieuw: regio (bezoekadres)
  phone: 70,
  mobile: 70,
  email: 90,
  admin: 70,
  active: 38,
  companyCode: 90,
};

  const searchInputStyle = {
    padding: "7px 10px",
    width: 180,
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    outline: "none",
    boxSizing: "border-box",
  };

  const inputStyle = {
    padding: "7px 10px",
    width: "100%",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    boxSizing: "border-box",
  };

  const buttonStyle = {
    padding: "7px 12px",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    cursor: "pointer",
    whiteSpace: "nowrap",
  };

  const buttonPrimary = (bg) => ({
    ...buttonStyle,
    background: bg,
    color: "white",
    border: "1px solid rgba(0,0,0,0.05)",
  });

  const buttonDisabledStyle = { ...buttonStyle, opacity: 0.55, cursor: "not-allowed" };

  const selectStyle = {
    height: 30,
    padding: "0 8px",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    width: "100%",
    boxSizing: "border-box",
  };

  const tableStyle = { width: "100%", borderCollapse: "separate", borderSpacing: 0, tableLayout: "fixed" };

  const thBase = {
    textAlign: "left",
    fontSize: 12,
    color: "#444",
    fontWeight: 700,
    padding: "8px 6px",
    borderBottom: "1px solid #e6e6e6",
    whiteSpace: "nowrap",
    position: "sticky",
    top: 0,
    zIndex: 5,
    background: "#fff",
    userSelect: "none",
  };

  const thClickable = { ...thBase, cursor: "pointer" };

  const filterThStyle = {
    ...thBase,
    fontWeight: 400,
    padding: "6px 6px 8px 6px",
    borderBottom: "1px solid #ededed",
    top: 32,
    zIndex: 4,
    cursor: "default",
  };

  const tdStyle = {
    padding: "7px 6px",
    borderBottom: "1px solid #efefef",
    fontSize: 13,
    color: "#222",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  const rowBgCursor = "#f3f7ff";
  const rowBgSelected = "#e6f0ff";

  function sortToggle(key) {
    setSort((s) => {
      if (s.key !== key) return { key, dir: "asc" };
      return { key, dir: s.dir === "asc" ? "desc" : "asc" };
    });
  }

  function sortArrow(key) {
    if (sort.key !== key) return "";
    return sort.dir === "asc" ? " ▲" : " ▼";
  }

const deleteEnabled = formHasId() && !isFormLocked;

  const searchNameOptions = useMemo(() => uniqSorted(items.map((x) => x.search_name || "")), [items]);
  const searchNameHasEmpty = useMemo(() => items.some((x) => !safeTrim(x.search_name || "")), [items]);

  const entityColOptions = ["O", "P"];

  const typeColOptions = useMemo(() => uniqSorted(items.map((x) => x.type || "")), [items]);
  const typeHasEmpty = useMemo(() => items.some((x) => !safeTrim(x.type || "")), [items]);

  const pcColOptions = useMemo(() => uniqSorted(items.map((x) => x.corr_postcode || "")), [items]);
  const pcHasEmpty = useMemo(() => items.some((x) => !safeTrim(x.corr_postcode || "")), [items]);

  const cityColOptions = useMemo(() => uniqSorted(items.map((x) => x.corr_city || "")), [items]);
  const cityHasEmpty = useMemo(() => items.some((x) => !safeTrim(x.corr_city || "")), [items]);

// ✅ nieuw: Regio (bezoekadres)
const regionColOptions = useMemo(() => uniqSorted(items.map((x) => x.visit_region || "")), [items]);
const regionHasEmpty = useMemo(() => items.some((x) => !safeTrim(x.visit_region || "")), [items]);

  const phoneColOptions = useMemo(() => uniqSorted(items.map((x) => x.phone || "")), [items]);
  const phoneHasEmpty = useMemo(() => items.some((x) => !safeTrim(x.phone || "")), [items]);

  const mobileColOptions = useMemo(() => uniqSorted(items.map((x) => x.mobile || "")), [items]);
  const mobileHasEmpty = useMemo(() => items.some((x) => !safeTrim(x.mobile || "")), [items]);

  const emailColOptions = useMemo(() => uniqSorted(items.map((x) => x.email || "")), [items]);
  const emailHasEmpty = useMemo(() => items.some((x) => !safeTrim(x.email || "")), [items]);

const adminColOptions = useMemo(() => uniqSorted(items.map((x) => x.administration_type || "")), [items]);
const adminHasEmpty = useMemo(() => items.some((x) => !safeTrim(x.administration_type || "")), [items]);

const companyCodeColOptions = useMemo(() => uniqSorted(items.map((x) => companyCodeLabel(x))), [items]);
const companyCodeHasEmpty = useMemo(() => items.some((x) => !companyCodeLabel(x)), [items]);

function openAiPopup() {
  if (isFormLocked) return;
  setAiText("");
  setAiOpen(true);
}

function closeAiPopup() {
  if (aiBusy) return;
  setAiText("");
  setAiOpen(false);
}

function appendAiConversation(question, answer) {
  const q = String(question || "").trim();
  const a = String(answer || "").trim();

  const block =
    `Vraag:\n${q || "-"}\n\n` +
    `----------------------------------------\n\n` +
    `Antwoord:\n${a || "-"}\n\n` +
    `========================================\n\n`;

  setAiText((prev) => {
    const cur = String(prev || "").trim();
    if (!cur) return block;
    return `${cur}\n\n${block}`;
  });
}

async function runAiTextAnalyseUnified() {
  if (isFormLocked) return;

  const sourceText = String(aiText || "").trim();
  if (!sourceText) {
    window.alert("Plak eerst tekst in het AI-venster.");
    return;
  }

  try {
    setAiBusy(true);
    setSaveError(null);
    setFlash("AI verwerkt tekst...");

    const token = localStorage.getItem("token");
    const tenant = localStorage.getItem("tenant") || "demo";

    const res = await fetch("/api/ai/organisations/extract", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        "x-tenant": tenant,
      },
      body: JSON.stringify({ text: sourceText }),
    });

    const raw = await res.text();
    let data = null;

    try {
      data = raw ? JSON.parse(raw) : null;
    } catch {
      throw new Error(raw || "Server gaf geen geldige JSON terug");
    }

    if (!res.ok || !data?.ok) {
      throw new Error(data?.error || "AI import mislukt");
    }

    const ai = data.data || {};

    setSelectedId(null);
    setMode("form");

    setForm((prev) => ({
      ...prev,
      ...emptyForm,
      name: ai.name || "",
      search_name: ai.search_name || ai.name || "",
      kvk: ai.kvk || "",
      vat_number: ai.vat_number || "",
      email: ai.email || "",
      website: ai.website || "",
      phone: ai.phone || "",
      mobile: ai.mobile || "",
      visit_street: ai.visit_street || "",
      visit_house_number: ai.visit_house_number || "",
      visit_addition: ai.visit_addition || "",
      visit_postcode: ai.visit_postcode || "",
      visit_city: ai.visit_city || "",
      visit_country: ai.visit_country || "",
      type: ai.type || "",
      administration_type: ai.administration_type || "",
      remarks: ai.remarks || "",
      active: true,
    }));

    const lines = [];
    lines.push("Tekst is geanalyseerd en de velden zijn ingevuld.");
    if (ai.name) lines.push(`Naam: ${ai.name}`);
    if (ai.search_name || ai.name) lines.push(`Zoeknaam: ${ai.search_name || ai.name}`);
    if (ai.email) lines.push(`E-mail: ${ai.email}`);
    if (ai.phone) lines.push(`Telefoon: ${ai.phone}`);
    if (ai.website) lines.push(`Website: ${ai.website}`);

    setAiText(lines.join("\n"));
    setFlash("AI-gegevens ingevuld ✓");
  } catch (err) {
    setAiText("FOUT:\n" + (err?.message || "Onbekende fout"));
    setFlash("");
  } finally {
    setAiBusy(false);
  }
}

async function runAiOrganisationCheckUnified() {
  if (isFormLocked) return;

  try {
    setAiBusy(true);
    setSaveError(null);
    setFlash("AI controleert organisatie...");

    const token = localStorage.getItem("token");
    const tenant = localStorage.getItem("tenant") || "demo";

    const res = await fetch("/api/ai/organisations/check", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        "x-tenant": tenant,
      },
      body: JSON.stringify({ organisation: form }),
    });

    const raw = await res.text();
    let data = null;

    try {
      data = raw ? JSON.parse(raw) : null;
    } catch {
      throw new Error(raw || "Server gaf geen geldige JSON terug");
    }

    if (!res.ok || !data?.ok) {
      throw new Error(data?.error || "AI controle mislukt");
    }

    setAiText(data.result || "Geen resultaat");
    setFlash("Organisatie gecontroleerd ✓");
  } catch (err) {
    setAiText("FOUT:\n" + (err?.message || "Onbekende fout"));
    setFlash("");
  } finally {
    setAiBusy(false);
  }
}

async function runAiTextAnalyse() {
  if (isFormLocked) return;

  const sourceText = String(aiText || "").trim();
  if (!sourceText) {
    window.alert("Plak eerst tekst in het AI-venster.");
    return;
  }

  try {
    setAiBusy(true);
    setSaveError(null);
    setFlash("AI verwerkt tekst...");

    const token = localStorage.getItem("token");
    const tenant = localStorage.getItem("tenant") || "demo";

    const res = await fetch("/api/ai/organisations/extract", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        "x-tenant": tenant,
      },
      body: JSON.stringify({ text: sourceText }),
    });

    const raw = await res.text();
    let data = null;

    try {
      data = raw ? JSON.parse(raw) : null;
    } catch {
      throw new Error(raw || "Server gaf geen geldige JSON terug");
    }

    if (!res.ok || !data?.ok) {
      throw new Error(data?.error || "AI import mislukt");
    }

    const ai = data.data || {};

    setSelectedId(null);
    setMode("form");

    setForm((prev) => ({
      ...prev,
      ...emptyForm,
      name: ai.name || "",
      search_name: ai.search_name || ai.name || "",
      kvk: ai.kvk || "",
      vat_number: ai.vat_number || "",
      email: ai.email || "",
      website: ai.website || "",
      phone: ai.phone || "",
      mobile: ai.mobile || "",
      visit_street: ai.visit_street || "",
      visit_house_number: ai.visit_house_number || "",
      visit_addition: ai.visit_addition || "",
      visit_postcode: ai.visit_postcode || "",
      visit_city: ai.visit_city || "",
      visit_country: ai.visit_country || "",
      type: ai.type || "",
      administration_type: ai.administration_type || "",
      remarks: ai.remarks || "",
      active: true,
    }));

    const lines = [];
    lines.push("Tekst is geanalyseerd en de velden zijn ingevuld.");
    if (ai.name) lines.push(`Naam: ${ai.name}`);
    if (ai.search_name || ai.name) lines.push(`Zoeknaam: ${ai.search_name || ai.name}`);
    if (ai.email) lines.push(`E-mail: ${ai.email}`);
    if (ai.phone) lines.push(`Telefoon: ${ai.phone}`);
    if (ai.website) lines.push(`Website: ${ai.website}`);

    setAiText(lines.join("\n"));
    setFlash("AI-gegevens ingevuld ✓");
  } catch (err) {
    setAiText("FOUT:\n" + (err?.message || "Onbekende fout"));
    setFlash("");
  } finally {
    setAiBusy(false);
  }
}

async function runAiOrganisationCheck() {
  try {
    setAiBusy(true);
    setFlash("AI controleert organisatie...");

    const token = localStorage.getItem("token");
    const tenant = localStorage.getItem("tenant") || "demo";

    const res = await fetch("/api/ai/organisations/check", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        "x-tenant": tenant,
      },
      body: JSON.stringify({ organisation: form }),
    });

    const raw = await res.text();
    let data = null;

    try {
      data = raw ? JSON.parse(raw) : null;
    } catch {
      throw new Error(raw || "Server gaf geen geldige JSON terug");
    }

    if (!res.ok || !data?.ok) {
      throw new Error(data?.error || "AI controle mislukt");
    }

    setAiText(data.result || "Geen resultaat");
    setFlash("Organisatie gecontroleerd ✓");
  } catch (err) {
    setAiText("FOUT:\n" + (err?.message || "Onbekende fout"));
    setFlash("");
  } finally {
    setAiBusy(false);
  }
}

function buildOrganisationAiContext() {
  const lines = [];

  if (safeTrim(form.name)) lines.push(`Naam: ${form.name}`);
  if (safeTrim(form.search_name)) lines.push(`Zoeknaam: ${form.search_name}`);
  if (safeTrim(form.type)) lines.push(`Type: ${form.type}`);
  if (safeTrim(form.administration_type)) lines.push(`Administratie type: ${form.administration_type}`);
  if (safeTrim(form.first_contact_date)) lines.push(`Datum 1e contact: ${form.first_contact_date}`);

  if (safeTrim(form.phone)) lines.push(`Telefoon: ${form.phone}`);
  if (safeTrim(form.mobile)) lines.push(`Mobiel: ${form.mobile}`);
  if (safeTrim(form.email)) lines.push(`E-mail: ${form.email}`);
  if (safeTrim(form.website)) lines.push(`Website: ${form.website}`);

  const visitAddress = joinSp([
    form.visit_street,
    form.visit_house_number,
    form.visit_addition,
  ]);
  if (safeTrim(visitAddress)) lines.push(`Bezoekadres: ${visitAddress}`);
  if (safeTrim(form.visit_postcode) || safeTrim(form.visit_city)) {
    lines.push(`Bezoek postcode/plaats: ${joinSp([form.visit_postcode, form.visit_city])}`);
  }
  if (safeTrim(form.visit_province)) lines.push(`Bezoek provincie: ${form.visit_province}`);
  if (safeTrim(form.visit_region)) lines.push(`Bezoek regio: ${form.visit_region}`);
  if (safeTrim(form.visit_country)) lines.push(`Bezoek land: ${form.visit_country}`);

  const corrAddress = joinSp([
    form.corr_street,
    form.corr_house_number,
    form.corr_addition,
  ]);
  if (safeTrim(corrAddress)) lines.push(`Correspondentieadres: ${corrAddress}`);
  if (safeTrim(form.corr_postcode) || safeTrim(form.corr_city)) {
    lines.push(`Correspondentie postcode/plaats: ${joinSp([form.corr_postcode, form.corr_city])}`);
  }
  if (safeTrim(form.corr_province)) lines.push(`Correspondentie provincie: ${form.corr_province}`);
  if (safeTrim(form.corr_region)) lines.push(`Correspondentie regio: ${form.corr_region}`);
  if (safeTrim(form.corr_country)) lines.push(`Correspondentie land: ${form.corr_country}`);

  if (safeTrim(form.bank)) lines.push(`Bank: ${form.bank}`);
  if (safeTrim(form.iban)) lines.push(`IBAN: ${form.iban}`);
  if (safeTrim(form.bic)) lines.push(`BIC: ${form.bic}`);
  if (safeTrim(form.account_name)) lines.push(`Ten name van: ${form.account_name}`);
  if (safeTrim(form.g_iban)) lines.push(`G-rekening IBAN: ${form.g_iban}`);
  if (safeTrim(form.g_percentage)) lines.push(`G-rekening %: ${form.g_percentage}`);
  if (safeTrim(form.sales_factor)) lines.push(`Verkoopfactor: ${form.sales_factor}`);

  if (safeTrim(form.kvk)) lines.push(`KvK: ${form.kvk}`);
  if (safeTrim(form.vat_number)) lines.push(`BTW-nummer: ${form.vat_number}`);
  if (safeTrim(form.vat_rate)) lines.push(`BTW-tarief: ${form.vat_rate}`);

  if (safeTrim(form.payment_terms)) lines.push(`Betalingsconditie: ${form.payment_terms}`);
  if (safeTrim(form.payment_method)) lines.push(`Betaalmethode: ${form.payment_method}`);
  if (safeTrim(form.payment_term_days)) lines.push(`Betaaltermijn dagen: ${form.payment_term_days}`);
  if (safeTrim(form.credit_limit)) lines.push(`Kredietlimiet: ${form.credit_limit}`);

  lines.push(`Correspondentie en akkoord via WhatsApp: ${form.correspondence_whatsapp ? "Ja" : "Nee"}`);
  lines.push(`Correspondentie en akkoord via E-mail: ${form.correspondence_email ? "Ja" : "Nee"}`);
  lines.push(`Digitaal factureren: ${form.invoice_digital ? "Ja" : "Nee"}`);
  lines.push(`Mailinglijst: ${form.mailing_list ? "Ja" : "Nee"}`);
  lines.push(`Actief: ${form.active ? "Ja" : "Nee"}`);

  if (safeTrim(form.remarks)) {
    lines.push(`Opmerking: ${form.remarks}`);
  }

  return lines.join("\n");
}

async function runAiSummary() {
  if (!formHasId()) {
    setAiText("Sla de organisatie eerst op voordat je een dossieroverzicht maakt.");
    setFlash("");
    return;
  }

  try {
    setAiBusy(true);
    setFlash("Dossieroverzicht wordt opgebouwd...");

    const dossier = await tryFetchJson([
      `/api/organisations/${form.id}/dossier`
    ]);

    if (!dossier?.ok) {
      throw new Error(dossier?.error || "Dossier ophalen mislukt");
    }

    const org = dossier.organisation || {};
    const contacts = Array.isArray(dossier.contacts) ? dossier.contacts : [];
    const notes = Array.isArray(dossier.notes_tasks) ? dossier.notes_tasks : [];
    const offers = Array.isArray(dossier.offers) ? dossier.offers : [];
    const invoices = Array.isArray(dossier.invoices) ? dossier.invoices : [];

    const firstFilled = (...values) => {
      for (const v of values) {
        const s = safeTrim(v);
        if (s) return s;
      }
      return "";
    };

    const formatAnyDate = (value) => {
      const s = safeTrim(value);
      if (!s) return "-";

      const d = new Date(s);
      if (Number.isNaN(d.getTime())) return s;

      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      const hh = String(d.getHours()).padStart(2, "0");
      const mi = String(d.getMinutes()).padStart(2, "0");

      if (hh === "00" && mi === "00") {
        return `${dd}-${mm}-${yyyy}`;
      }

      return `${dd}-${mm}-${yyyy} ${hh}:${mi}`;
    };

    const translateStatus = (value, kind = "") => {
      const s = safeTrim(value).toUpperCase();
      if (!s) return "-";

      if (kind === "offer") {
        if (s === "DRAFT") return "Concept";
        if (s === "APPROVED") return "Goedgekeurd";
        if (s === "SENT") return "Verzonden";
        if (s === "ACCEPTED") return "Geaccepteerd";
        if (s === "REJECTED") return "Afgewezen";
      }

      if (kind === "invoice") {
        if (s === "DRAFT") return "Concept";
        if (s === "FINAL") return "Gefactureerd";
        if (s === "PARTIAL") return "Deelbetaling";
        if (s === "PAID") return "Betaald";
        if (s === "SENT") return "Verzonden";
      }

      if (s === "OPEN") return "Open";
      if (s === "DONE") return "Gereed";
      if (s === "CLOSED") return "Gesloten";

      return safeTrim(value) || "-";
    };

    const translateNoteType = (value) => {
      const s = safeTrim(value).toLowerCase();
      if (!s) return "-";
      if (s === "note") return "Notitie";
      if (s === "task") return "Taak";
      if (s === "todo") return "Taak";
      if (s === "call") return "Telefoontje";
      if (s === "email") return "E-mail";
      if (s === "appointment") return "Afspraak";
      return safeTrim(value);
    };

    const formatAmount = (...values) => {
      let picked = "";

      for (const v of values) {
        const s = safeTrim(v);
        if (s) {
          picked = s;
          break;
        }
      }

      if (!picked) return "-";

      return formatCurrencyNL(picked);
    };

    const pickBestTotal = (row) => {
      const total = Number(String(firstFilled(
        row.total,
        row.total_incl_vat,
        row.total_including_vat,
        row.grand_total,
        row.amount
      )).replace(",", "."));

      const subtotal = Number(String(firstFilled(
        row.subtotal,
        row.sub_total,
        row.amount_ex_vat
      )).replace(",", "."));

      const vatTotal = Number(String(firstFilled(
        row.vat_total,
        row.vat,
        row.amount_vat
      )).replace(",", "."));

      const computed = Number.isFinite(subtotal) && Number.isFinite(vatTotal)
        ? subtotal + vatTotal
        : NaN;

      if (Number.isFinite(computed) && computed > 0) {
        return computed;
      }

      if (Number.isFinite(total)) {
        return total;
      }

      return "";
    };

    const lines = [];

    lines.push("DOSSIEROVERZICHT");
    lines.push("");

    lines.push("1. HUIDIGE INFORMATIE");
    lines.push(`Naam: ${safeTrim(org.name) || "-"}`);
    lines.push(`Zoeknaam: ${safeTrim(org.search_name) || "-"}`);
    lines.push(`Type: ${safeTrim(org.type) || "-"}`);
    lines.push(`Administratie type: ${safeTrim(org.administration_type) || "-"}`);
    lines.push(`Telefoon: ${safeTrim(org.phone) || "-"}`);
    lines.push(`Mobiel: ${safeTrim(org.mobile) || "-"}`);
    lines.push(`E-mail: ${safeTrim(org.email) || "-"}`);
    lines.push(`Website: ${safeTrim(org.website) || "-"}`);
    lines.push(`KvK: ${safeTrim(org.kvk) || "-"}`);
    lines.push(`BTW-nummer: ${safeTrim(org.vat_number) || "-"}`);

    const visitAddress = [
      joinSp([org.visit_street, org.visit_house_number, org.visit_addition]),
      joinSp([org.visit_postcode, org.visit_city]),
      joinSp([org.visit_region, org.visit_country], ", "),
    ].filter((x) => safeTrim(x)).join(" | ");
    lines.push(`Bezoekadres: ${visitAddress || "-"}`);

    if (safeTrim(org.remarks)) {
      lines.push(`Opmerking: ${org.remarks}`);
    }

    lines.push("");
    lines.push("2. CONTACTPERSONEN");

    if (!contacts.length) {
      lines.push("Geen contactpersonen gevonden.");
    } else {
      contacts.forEach((c, index) => {
        const displayName = firstFilled(
          c.search_name,
          c.name,
          joinSp([c.first_name, c.last_name]),
          c.full_name,
          c.display_name
        ) || "(zonder naam)";

        const fullName = firstFilled(
          c.name,
          joinSp([c.first_name, c.last_name]),
          c.full_name,
          c.display_name,
          c.search_name
        );

        const functionName = firstFilled(
          c.function,
          c.job_title,
          c.role,
          c.position,
          c.department
        );

        lines.push(`${index + 1}. ${displayName}`);
        lines.push(`   Naam: ${fullName || "-"}`);
        lines.push(`   Functie: ${functionName || "-"}`);
        lines.push(`   Telefoon: ${firstFilled(c.phone, c.telephone) || "-"}`);
        lines.push(`   Mobiel: ${firstFilled(c.mobile, c.cellphone, c.mobiel) || "-"}`);
        lines.push(`   E-mail: ${firstFilled(c.email, c.mail) || "-"}`);
      });
    }

    lines.push("");
    lines.push("3. NOTITIES EN TAKEN");

    if (!notes.length) {
      lines.push("Geen notities of taken gevonden.");
    } else {
      notes.forEach((n, index) => {
        const dt = formatAnyDate(
          firstFilled(n.updated_at, n.created_at, n.note_date, n.task_date, n.date)
        );

        const title = firstFilled(
          n.title,
          n.subject,
          n.name,
          n.task_title,
          n.note_title
        );

        const type = firstFilled(
          n.type,
          n.kind,
          n.entry_type,
          n.task_type,
          n.note_type,
          n.category
        );

        const content = firstFilled(
          n.note,
          n.notes,
          n.description,
          n.text,
          n.remarks,
          n.content,
          n.body
        );

        lines.push(`${index + 1}. ${dt}`);
        lines.push(`   Titel: ${title || "-"}`);
        lines.push(`   Type: ${translateNoteType(type)}`);
        lines.push(`   Status: ${translateStatus(firstFilled(n.status, n.state))}`);
        lines.push(`   Inhoud: ${content || "-"}`);
      });
    }

    lines.push("");
    lines.push("4. LAATSTE OFFERTE");

    if (!offers.length) {
      lines.push("Geen offertes gevonden.");
    } else {
      const o = offers[0];
      lines.push(`Offertenummer: ${firstFilled(o.dossier_number, o.offer_number, o.offerte_nummer, o.number, o.reference, o.name, o.title) || "-"}`);
      lines.push(`Datum: ${firstFilled(o.dossier_date) || formatAnyDate(firstFilled(o.offer_date, o.date, o.invoice_date, o.valid_until, o.updated_at, o.created_at))}`);
      lines.push(`Status: ${translateStatus(firstFilled(o.status), "offer")}`);
      lines.push(`Omschrijving: ${firstFilled(o.dossier_description, o.subject, o.title, o.description, o.notes, o.reference) || "-"}`);
      lines.push(`Totaal: ${formatAmount(firstFilled(o.dossier_total, pickBestTotal(o)))}`);
      lines.push("");
      lines.push(`Aantal offertes in totaal: ${offers.length}`);
    }

    lines.push("");
    lines.push("5. LAATSTE FACTUUR");

    if (!invoices.length) {
      lines.push("Geen facturen gevonden.");
    } else {
      const inv = invoices[0];
      lines.push(`Factuurnummer: ${firstFilled(inv.dossier_number, inv.invoice_number, inv.factuurnummer, inv.number, inv.reference) || "-"}`);
      lines.push(`Datum: ${firstFilled(inv.dossier_date) || formatAnyDate(firstFilled(inv.invoice_date, inv.date, inv.updated_at, inv.created_at))}`);
      lines.push(`Status: ${translateStatus(firstFilled(inv.status), "invoice")}`);
      lines.push(`Omschrijving: ${firstFilled(inv.dossier_description, inv.subject, inv.title, inv.description, inv.notes, inv.reference) || "-"}`);
      lines.push(`Totaal: ${formatAmount(firstFilled(inv.dossier_total, pickBestTotal(inv)))}`);
      lines.push("");
      lines.push(`Aantal facturen in totaal: ${invoices.length}`);
    }

    setAiText(lines.join("\n"));
    setFlash("Dossieroverzicht gemaakt ✓");
  } catch (err) {
    setAiText("FOUT: " + (err?.message || "Onbekende fout"));
    setFlash("");
  } finally {
    setAiBusy(false);
  }
}

async function runAiChat() {
  if (isFormLocked) return;

  const question = String(aiText || "").trim();

  if (!question) {
    setFlash("");
    setAiText("Typ eerst een vraag voor Chat met AI.\n\nBijvoorbeeld:\nWat ontbreekt er nog voor facturatie?");
    setTimeout(() => {
      try {
        aiTextRef.current?.focus?.({ preventScroll: true });
      } catch {
        try {
          aiTextRef.current?.focus?.();
        } catch {}
      }
    }, 0);
    return;
  }

  try {
    setAiBusy(true);
    setSaveError(null);
    setFlash("AI beantwoordt vraag...");

    const res = await tryFetchJson(
      ["/api/ai/organisations/check"],
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          organisation: form,
          mode: "chat",
          question,
        }),
      }
    );

    if (!res?.ok) {
      throw new Error(res?.error || "AI chat mislukt");
    }

    const answer = res.result || "Geen antwoord";
    appendAiConversation(question, answer);
    setFlash("AI chat gereed ✓");

    setTimeout(() => {
      try {
        aiTextRef.current?.focus?.({ preventScroll: true });
      } catch {
        try {
          aiTextRef.current?.focus?.();
        } catch {}
      }
    }, 0);
  } catch (err) {
    appendAiConversation(question, "FOUT: " + (err?.message || "Onbekende fout"));
    setFlash("");
  } finally {
    setAiBusy(false);
  }
}

function printAiDocument() {
  const title = "StaffIQ AI Document";
  const content = String(aiText || "").trim() || "Geen inhoud om te printen.";

  const html = `
    <html>
      <head>
        <title>${title}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            padding: 24px;
            color: #111;
            line-height: 1.45;
            white-space: pre-wrap;
          }
          h1 {
            font-size: 20px;
            margin: 0 0 18px 0;
          }
        </style>
      </head>
      <body>
        <h1>${title}</h1>
        <div>${content
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")}</div>
      </body>
    </html>
  `;

  const win = window.open("", "_blank", "width=900,height=700");
  if (!win) return;

  win.document.open();
  win.document.write(html);
  win.document.close();
  win.focus();

  setTimeout(() => {
    try {
      win.print();
    } catch {}
  }, 300);
}

function startAiSpeak() {
  if (!voice.supported || voice.listening) return;

  try {
    aiTextRef.current?.focus?.({ preventScroll: true });
  } catch {
    try {
      aiTextRef.current?.focus?.();
    } catch {}
  }

  voice.startOnce();
}

  return (
    <div>
<div
  style={{
    display: "grid",
    gridTemplateColumns: isMobile
      ? "1fr"
      : `minmax(0, 1fr) minmax(${RIGHT_WIDTH}px, ${RIGHT_WIDTH}px)`,
    columnGap: GAP,
    rowGap: isMobile ? 22 : 0,
    alignItems: "start",
    minWidth: 0,
  }}
>
        {/* LINKS */}
        <div style={{ minWidth: 0 }}>

<h1 ref={pageTopRef} style={{ marginTop: 0, marginBottom: 10 }}>
  Relaties <span style={{ fontSize: 12, opacity: 0.5 }}>{VERSION}</span>
</h1>

<>
  {!isMobile ? (
    <div
      style={{
        display: "flex",
        gap: 8,
        alignItems: "center",
        flexWrap: "nowrap",
      }}
    >
      <input
        ref={searchRef}
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setMode("search");
          setRemarkEditor(false);
        }}
        placeholder="Zoek…"
        style={searchInputStyle}
        onFocus={() => {
          setMode("search");
          setRemarkEditor(false);
        }}
      />

      <div
        title={`Aantal regels in het grid: ${gridCount}`}
        style={{
          minWidth: 52,
          height: 32,
          padding: "0 10px",
          border: "1px solid #d9d9d9",
          borderRadius: 4,
          background: "#f7fafc",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 13,
          fontWeight: 700,
          color: "#1f4e79",
          boxSizing: "border-box",
          whiteSpace: "nowrap",
        }}
      >
        {gridCount}
      </div>

      <button
        onMouseDown={(e) => e.preventDefault()}
        onTouchStart={(e) => e.preventDefault()}
        onClick={startSpeakSmart}
        disabled={!voice.supported || voice.listening}
        style={!voice.supported || voice.listening ? buttonDisabledStyle : buttonStyle}
        title={remarkEditor ? "In Opmerking: 1x klikken = door-dicteren (stop/klaar)" : "1x klikken = 1 commando of invul-tekst dicteren"}
      >
        {voice.listening ? "🎤 Luistert…" : "🎤 Spreek"}
      </button>

<button
        onClick={newItem}
        disabled={isReadonlyRole}
        style={
          isReadonlyRole
            ? { ...buttonStyle, opacity: 0.5, cursor: "not-allowed" }
            : buttonStyle
        }
      >
        ➕ Nieuw
      </button>

      <button onClick={vernieuwList} style={buttonStyle}>
        🔄 Vernieuw
      </button>

<button
        onClick={openAiPopup}
        disabled={isFormLocked}
        style={
          isFormLocked
            ? { ...buttonStyle, opacity: 0.5, cursor: "not-allowed" }
            : buttonStyle
        }
      >
        ✨ AI
      </button>
    </div>
  ) : (
    <>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) 58px",
          gap: 6,
          marginBottom: 8,
          alignItems: "center",
        }}
      >
        <input
          ref={searchRef}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setMode("search");
            setRemarkEditor(false);
          }}
          placeholder="Zoek…"
          style={{
            ...searchInputStyle,
            width: "100%",
            minWidth: 0,
          }}
          onFocus={() => {
            setMode("search");
            setRemarkEditor(false);
          }}
        />

<div
          title={`Aantal regels in het grid: ${gridCount}`}
          style={{
            height: 34,
            minHeight: 34,
            padding: "0 8px",
            border: "1px solid #d9d9d9",
            borderRadius: 4,
            background: "#f7fafc",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 13,
            fontWeight: 700,
            color: "#1f4e79",
            boxSizing: "border-box",
            whiteSpace: "nowrap",
            lineHeight: "1",
          }}
        >
          {gridCount}
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr 1fr",
          gap: 6,
        }}
      >
        <button
          onMouseDown={(e) => e.preventDefault()}
          onTouchStart={(e) => e.preventDefault()}
          onClick={startSpeakSmart}
          disabled={!voice.supported || voice.listening}
          style={{
            ...( !voice.supported || voice.listening ? buttonDisabledStyle : buttonStyle ),
            padding: "10px 8px",
            minWidth: 0,
            fontSize: 13,
          }}
          title={remarkEditor ? "In Opmerking: 1x klikken = door-dicteren (stop/klaar)" : "1x klikken = 1 commando of invul-tekst dicteren"}
        >
          {voice.listening ? "🎤..." : "🎤 Spreek"}
        </button>

<button
          onClick={newItem}
          disabled={isReadonlyRole}
          style={{
            ...(isReadonlyRole
              ? { ...buttonStyle, opacity: 0.5, cursor: "not-allowed" }
              : buttonStyle),
            padding: "10px 8px",
            minWidth: 0,
            fontSize: 13,
          }}
        >
          ➕ Nieuw
        </button>

        <button
          onClick={vernieuwList}
          style={{
            ...buttonStyle,
            padding: "10px 8px",
            minWidth: 0,
            fontSize: 13,
          }}
        >
          🔄 Vernieuw
        </button>

<button
          onClick={openAiPopup}
          disabled={isFormLocked}
          style={{
            ...(isFormLocked
              ? { ...buttonStyle, opacity: 0.5, cursor: "not-allowed" }
              : buttonStyle),
            padding: "10px 8px",
            minWidth: 0,
            fontSize: 13,
          }}
        >
          ✨ AI
        </button>
      </div>
    </>
  )}
</>

          <div style={{ marginTop: 10 }}>
            {loading && <div>Laden…</div>}
            {error && <div style={{ color: "crimson" }}>{error}</div>}
            {flash && <div style={{ marginBottom: 8, color: "green" }}>{flash}</div>}
            {saveError && <div style={{ marginBottom: 8, color: "crimson" }}>{saveError}</div>}
            {voice.error && !hideVoiceError && <div style={{ color: "crimson" }}>Spraakfout: {voice.error}</div>}
          </div>

          {!loading && !error && (
            <div style={{ marginTop: 8, minWidth: 0 }}>
              <div
                ref={midScrollRef}
                style={{
                  overflowX: "scroll",
                  overflowY: "auto",
                  maxHeight: `calc(100vh - ${MID_BOTTOM_OFFSET}px)`,
                  border: "1px solid #f0f0f0",
                  borderRadius: 6,
                  background: "#fff",
                }}
              >
                <div style={{ minWidth: TABLE_MIN_WIDTH }}>
                  <table style={tableStyle}>
<colgroup>
  <col style={{ width: colWidths.search }} />
  {showEntityChoice && <col style={{ width: colWidths.entity }} />}
  <col style={{ width: colWidths.type }} />
  <col style={{ width: colWidths.pc }} />
  <col style={{ width: colWidths.city }} />
  <col style={{ width: colWidths.region }} /> {/* ✅ nieuw */}
  <col style={{ width: colWidths.phone }} />
  <col style={{ width: colWidths.mobile }} />
  <col style={{ width: colWidths.email }} />
  <col style={{ width: colWidths.admin }} />
  <col style={{ width: colWidths.active }} />
  <col style={{ width: colWidths.companyCode }} />
</colgroup>

                    <thead>
                      <tr>
                        <th style={thClickable} onClick={() => sortToggle("search")}>
                          Zoeknaam{sortArrow("search")}
                        </th>
                        {showEntityChoice && (
                          <th style={thClickable} onClick={() => sortToggle("entity")}>
                            O/P{sortArrow("entity")}
                          </th>
                        )}
                        <th style={thClickable} onClick={() => sortToggle("type")}>
                          Soort{sortArrow("type")}
                        </th>
                        <th style={thClickable} onClick={() => sortToggle("pc")}>
                          Postcode{sortArrow("pc")}
                        </th>
<th style={thClickable} onClick={() => sortToggle("city")}>
  Plaats{sortArrow("city")}
</th>
<th style={thClickable} onClick={() => sortToggle("region")}>
  Regio{sortArrow("region")}
</th>
<th style={thClickable} onClick={() => sortToggle("phone")}>
  Telefoon{sortArrow("phone")}
</th>
<th style={thClickable} onClick={() => sortToggle("mobile")}>
  Mobiel{sortArrow("mobile")}
</th>
                        <th style={thClickable} onClick={() => sortToggle("email")}>
                          E-mail{sortArrow("email")}
                        </th>
                        <th style={thClickable} onClick={() => sortToggle("admin")}>
                          Admin.type{sortArrow("admin")}
                        </th>
                        <th style={thClickable} onClick={() => sortToggle("active")}>
                          Actief{sortArrow("active")}
                        </th>
                        <th style={thClickable} onClick={() => sortToggle("company_code")}>
                          Bedrijfscode{sortArrow("company_code")}
                        </th>
                      </tr>

                      <tr>
                        <th style={filterThStyle}>
                          <select value={searchNameFilter} onChange={(e) => setSearchNameFilter(e.target.value)} style={selectStyle}>
                            <option value="">(alle)</option>
                            {searchNameHasEmpty && <option value={EMPTY_VAL}>(leeg)</option>}
                            {searchNameOptions.map((x) => (
                              <option key={x} value={x}>
                                {x}
                              </option>
                            ))}
                          </select>
                        </th>

                        {showEntityChoice && (
                          <th style={filterThStyle}>
                            <select value={entityFilter} onChange={(e) => setEntityFilter(e.target.value)} style={selectStyle}>
                              <option value="">(alle)</option>
                              {entityColOptions.map((x) => (
                                <option key={x} value={x}>
                                  {x}
                                </option>
                              ))}
                            </select>
                          </th>
                        )}

                        <th style={filterThStyle}>
                          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} style={selectStyle}>
                            <option value="">(alle)</option>
                            {typeHasEmpty && <option value={EMPTY_VAL}>(leeg)</option>}
                            {typeColOptions.map((x) => (
                              <option key={x} value={x}>
                                {x}
                              </option>
                            ))}
                          </select>
                        </th>

                        <th style={filterThStyle}>
                          <select value={pcFilter} onChange={(e) => setPcFilter(e.target.value)} style={selectStyle}>
                            <option value="">(alle)</option>
                            {pcHasEmpty && <option value={EMPTY_VAL}>(leeg)</option>}
                            {pcColOptions.map((x) => (
                              <option key={x} value={x}>
                                {x}
                              </option>
                            ))}
                          </select>
                        </th>

<th style={filterThStyle}>
  <select value={cityFilter} onChange={(e) => setCityFilter(e.target.value)} style={selectStyle}>
    <option value="">(alle)</option>
    {cityHasEmpty && <option value={EMPTY_VAL}>(leeg)</option>}
    {cityColOptions.map((x) => (
      <option key={x} value={x}>
        {x}
      </option>
    ))}
  </select>
</th>

<th style={filterThStyle}>
  <select value={regionFilter} onChange={(e) => setRegionFilter(e.target.value)} style={selectStyle}>
    <option value="">(alle)</option>
    {regionHasEmpty && <option value={EMPTY_VAL}>(leeg)</option>}
    {regionColOptions.map((x) => (
      <option key={x} value={x}>
        {x}
      </option>
    ))}
  </select>
</th>

<th style={filterThStyle}>
  <select value={phoneFilter} onChange={(e) => setPhoneFilter(e.target.value)} style={selectStyle}>
    <option value="">(alle)</option>
    {phoneHasEmpty && <option value={EMPTY_VAL}>(leeg)</option>}
    {phoneColOptions.map((x) => (
      <option key={x} value={x}>
        {x}
      </option>
    ))}
  </select>
</th>

<th style={filterThStyle}>
  <select value={mobileFilter} onChange={(e) => setMobileFilter(e.target.value)} style={selectStyle}>
    <option value="">(alle)</option>
    {mobileHasEmpty && <option value={EMPTY_VAL}>(leeg)</option>}
    {mobileColOptions.map((x) => (
      <option key={x} value={x}>
        {x}
      </option>
    ))}
  </select>
</th>

                        <th style={filterThStyle}>
                          <select value={emailFilter} onChange={(e) => setEmailFilter(e.target.value)} style={selectStyle}>
                            <option value="">(alle)</option>
                            {emailHasEmpty && <option value={EMPTY_VAL}>(leeg)</option>}
                            {emailColOptions.map((x) => (
                              <option key={x} value={x}>
                                {x}
                              </option>
                            ))}
                          </select>
                        </th>

                        <th style={filterThStyle}>
                          <select value={adminFilter} onChange={(e) => setAdminFilter(e.target.value)} style={selectStyle}>
                            <option value="">(alle)</option>
                            {adminHasEmpty && <option value={EMPTY_VAL}>(leeg)</option>}
                            {adminColOptions.map((x) => (
                              <option key={x} value={x}>
                                {x}
                              </option>
                            ))}
                          </select>
                        </th>

                        <th style={filterThStyle}>
                          <select value={activeFilter} onChange={(e) => setActiveFilter(e.target.value)} style={selectStyle}>
                            <option value="">(alle)</option>
                            <option value="j">j</option>
                            <option value="n">n</option>
                          </select>
                        </th>

                        <th style={filterThStyle}>
                          <select value={companyCodeFilter} onChange={(e) => setCompanyCodeFilter(e.target.value)} style={selectStyle}>
                            <option value="">(alle)</option>
                            {companyCodeHasEmpty && <option value={EMPTY_VAL}>(leeg)</option>}
                            {companyCodeColOptions.map((x) => (
                              <option key={x} value={x}>
                                {x}
                              </option>
                            ))}
                          </select>
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {sortedItems.map((it, idx) => {
                        const isCursor = idx === listIndex;
                        const isSelected = selectedId === it.id;

                        let bg = "transparent";
                        if (isSelected) bg = rowBgSelected;
                        else if (isCursor) bg = rowBgCursor;

const zoek = it.search_name || it.name || "-";
const entity = relationEntityLabel(it.relation_entity_type || "company");
const soort = it.type || "-";
const pc = it.corr_postcode || "-";
const plaats = it.corr_city || "-";
const regio = it.visit_region || "-"; // ✅ nieuw: bezoekadres-regio
const tel = it.phone || "-";
const mob = it.mobile || "-";
const mail = it.email || "-";
const adm = it.administration_type || "-";
const act = activeToBool(it);
const bedrijfscode = companyCodeLabel(it) || "-";

                        // ---------------------------------------------------
  // SUBSECTION 8.12: RENDER
  // JSX-opbouw van linker navigatie, middenlijst en
  // rechter detailpaneel.
  // ---------------------------------------------------

  return (
                          <tr
                            key={it.id}
                            ref={(el) => (rowRefs.current[idx] = el)}
onClick={() => {
  setListIndex(idx);
  loadItemFromRow(it);
  setMode("form");
  setRemarkEditor(false);

  if (window.innerWidth <= 768) {
    focusFirstRightFieldMobile();
  } else {
    setTimeout(() => {
      try {
        if (rightScrollRef.current) {
          rightScrollRef.current.scrollTop = 0;
        }
      } catch {}
    }, 0);

    focusFirstRightField();
  }
}}
                            style={{ cursor: "pointer", backgroundColor: bg }}
                          >
                            <td style={tdStyle} title={zoek}>
                              {zoek}
                            </td>
                            {showEntityChoice && (
                              <td style={{ ...tdStyle, textAlign: "center" }} title={entity === "P" ? "Particulier" : "Organisatie"}>
                                {entity}
                              </td>
                            )}
                            <td style={tdStyle} title={soort}>
                              {soort}
                            </td>
                            <td style={tdStyle} title={pc}>
                              {pc}
                            </td>
<td style={tdStyle} title={plaats}>
  {plaats}
</td>
<td style={tdStyle} title={regio}>
  {regio}
</td>
<td style={tdStyle} title={tel}>
  {tel && tel !== "-" ? (
    <button
      type="button"
      onClick={(e) => handlePhoneGridClick(e, tel)}
      style={{
        border: "none",
        background: "transparent",
        padding: 0,
        margin: 0,
        color: "#1f4e79",
        textDecoration: "underline",
        cursor: "pointer",
        font: "inherit",
      }}
      title={`Bellen: ${tel}`}
    >
      {tel}
    </button>
  ) : (
    "-"
  )}
</td>

<td style={tdStyle} title={mob}>
  {mob && mob !== "-" ? (
    <button
      type="button"
      onClick={(e) => handleMobileGridClick(e, mob)}
      style={{
        border: "none",
        background: "transparent",
        padding: 0,
        margin: 0,
        color: "#1f4e79",
        textDecoration: "underline",
        cursor: "pointer",
        font: "inherit",
      }}
      title={`Bellen / SMS / WhatsApp: ${mob}`}
    >
      {mob}
    </button>
  ) : (
    "-"
  )}
</td>

                            <td style={tdStyle} title={mail}>
                              {mail && mail !== "-" ? (
                                <button
                                  type="button"
                                  onClick={(e) => handleEmailGridClick(e, mail)}
                                  style={{
                                    border: "none",
                                    background: "transparent",
                                    padding: 0,
                                    margin: 0,
                                    color: "#1f4e79",
                                    textDecoration: "underline",
                                    cursor: "pointer",
                                    font: "inherit",
                                  }}
                                  title={`E-mailen: ${mail}`}
                                >
                                  {mail}
                                </button>
                              ) : (
                                "-"
                              )}
                            </td>
                            <td style={tdStyle} title={adm}>
                              {adm}
                            </td>
                            <td style={{ ...tdStyle, textAlign: "center" }} title={act ? "actief" : "inactief"}>
                              <input type="checkbox" checked={act} readOnly />
                            </td>
                            <td style={tdStyle} title={bedrijfscode}>
                              {bedrijfscode}
                            </td>
                          </tr>
                        );
                      })}

                      {sortedItems.length === 0 && (
                        <tr>
                          <td colSpan={showEntityChoice ? 11 : 10} style={{ padding: 12, opacity: 0.7 }}>
                            Geen resultaten (filters te streng?)
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

{/* RECHTS */}
<div
          ref={rightScrollRef}
          style={{
            width: isMobile ? "100%" : 320,
            minWidth: isMobile ? 0 : 320,
            maxWidth: isMobile ? "100%" : 320,
            position: isMobile ? "relative" : "sticky",
            top: isMobile ? "auto" : 0,
            alignSelf: "start",
            maxHeight: isMobile ? "none" : "calc(100vh - 40px)",
            overflowY: isMobile ? "visible" : "auto",
            overflowAnchor: "none",
            background: "linear-gradient(180deg, #0b4f7a 0%, #083b5c 100%)",
            borderRadius: 10,
            padding: 10,
            boxSizing: "border-box",
            marginTop: isMobile ? 6 : 0,
            paddingTop: isMobile ? 6 : 10,
          }}
        >
          <div style={{ background: "white", borderRadius: 10, padding: 12, boxSizing: "border-box", overflowAnchor: "none" }}>
            <h3 style={{ marginTop: 0, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>Relatie</span>
              {remarkEditor && (
                <button
                  onClick={() => {
                    closeRemarkEditorTo(remarkSmallRef);
                  }}
                  style={buttonStyle}
                  title="Terug naar normaal formulier"
                >
                  ↩ Terug
                </button>
              )}
            </h3>

            {remarkEditor ? (
              <div style={{ display: "grid", gap: 10 }}>
                <div style={{ fontSize: 13, opacity: 0.8 }}>
                  Opmerking-editor (spreek: door-dicteren) — zeg <b>punt</b> voor “.”.
                </div>

                <textarea
                  ref={remarkBigRef}
                  value={form.remarks}
                  onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                  rows={15}
                  style={{
                    padding: "10px 10px",
                    width: "100%",
                    border: "1px solid #d9d9d9",
                    borderRadius: 6,
                    background: "white",
                    resize: "vertical",
                    boxSizing: "border-box",
                    minHeight: "60vh",
                    fontSize: 14,
                    lineHeight: 1.4,
                  }}
                  onBlur={() => {
                    remarkDictateActiveRef.current = false;
                  }}
                />
<div
  style={{
    background: "white",
    paddingTop: 10,
    marginTop: 6,
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    borderTop: "1px solid #eee",
  }}
>
<button 
  onClick={saveItem} 
  disabled={isFormLocked}
  style={
    isFormLocked
      ? { ...buttonPrimary("#2f7dd1"), opacity: 0.5, cursor: "not-allowed" }
      : buttonPrimary("#2f7dd1")
  }
>
  💾 Opslaan
</button>

<button
  onClick={deleteItem}
  disabled={isFormLocked || !deleteEnabled}
  style={
    isFormLocked || !deleteEnabled
      ? { ...buttonPrimary("#d64545"), opacity: 0.5, cursor: "not-allowed" }
      : buttonPrimary("#d64545")
  }
>
  🗑 Verwijderen
</button>
</div>
              </div>
            ) : (

<fieldset
  disabled={isFormLocked}
  style={{
    border: "none",
    padding: 0,
    margin: 0,
    minWidth: 0,
  }}
>

<div style={{ display: "grid", gap: 8 }}>
    {showEntityChoice && (
      <>
        <label style={{ fontSize: 13, color: "#333" }}>Organisatie / Particulier</label>
        <select
          ref={entityTypeRef}
          value={currentRelationEntityType}
          onChange={(e) => {
            const nextType = e.target.value;

            setTouched((x) => ({
              ...x,
              search_name: false,
              account_name: false,
            }));

            setForm((prev) => ({
              ...prev,
              relation_entity_type: nextType,
              parent_organisation_id: nextType === "private" ? "" : prev.parent_organisation_id,
            }));
          }}
          style={selectStyle}
        >
          <option value="company">Organisatie</option>
          <option value="private">Particulier</option>
        </select>

        <hr style={{ margin: "10px 0" }} />
      </>
    )}

    <label style={{ fontSize: 13, color: "#333" }}>Bedrijfscode</label>
    <select
      value={form.company_code_id || firstCompanyCodeIdFromScope(companyCodes)}
      onChange={(e) => setForm({ ...form, company_code_id: e.target.value })}
      style={selectStyle}
    >
      {selectedCompanyCodesForForm(companyCodes).map((cc) => (
        <option key={cc.id} value={cc.id}>
          {safeTrim(cc.name || cc.code || "")}
        </option>
      ))}
    </select>

    <hr style={{ margin: "10px 0" }} />

    {isPrivateRelation ? (
      <>
        <label style={{ fontSize: 13, color: "#333" }}>Voornaam</label>
        <input
          ref={firstNameRef}
          value={form.first_name || ""}
          onChange={(e) => setForm({ ...form, first_name: e.target.value })}
          style={inputStyle}
        />

        <label style={{ fontSize: 13, color: "#333" }}>Voorletters</label>
        <input
          ref={initialsRef}
          value={form.initials || ""}
          onChange={(e) => setForm({ ...form, initials: e.target.value })}
          style={inputStyle}
        />

        <label style={{ fontSize: 13, color: "#333" }}>Tussenvoegsel(s)</label>
        <input
          ref={namePrefixRef}
          value={form.name_prefix || ""}
          onChange={(e) => setForm({ ...form, name_prefix: e.target.value })}
          style={inputStyle}
        />

        <label style={{ fontSize: 13, color: "#333" }}>Achternaam</label>
        <input
          ref={lastNameRef}
          value={form.last_name || ""}
          onChange={(e) => setForm({ ...form, last_name: e.target.value })}
          style={inputStyle}
        />

        <label style={{ fontSize: 13, color: "#333" }}>Geslacht (m/v/o)</label>
        <select
          ref={genderRef}
          value={form.gender || ""}
          onChange={(e) => setForm({ ...form, gender: e.target.value })}
          style={selectStyle}
        >
          <option value="">(geen)</option>
          <option value="m">m</option>
          <option value="v">v</option>
          <option value="o">o</option>
        </select>

        <label style={{ fontSize: 13, color: "#333" }}>Geboortedatum</label>
        <input
          ref={birthDateRef}
          type="date"
          value={form.birth_date || ""}
          onChange={(e) => setForm({ ...form, birth_date: e.target.value })}
          style={inputStyle}
        />

        <label style={{ fontSize: 13, color: "#333" }}>Aanhef</label>
        <input
          ref={salutationRef}
          list="org_salutation_list"
          value={form.salutation || ""}
          onChange={(e) => setForm({ ...form, salutation: e.target.value })}
          style={inputStyle}
          placeholder="Bijv. Geachte of Beste"
        />
        <datalist id="org_salutation_list">
          <option value="Geachte" />
          <option value="Beste" />
        </datalist>

        <label style={{ fontSize: 13, color: "#333" }}>Aanschrijving</label>
        <input
          ref={attentionToRef}
          list="org_attention_list"
          value={form.attention_to || ""}
          onChange={(e) => setForm({ ...form, attention_to: e.target.value })}
          style={inputStyle}
          placeholder="Bijv. De heer, Mevrouw of T.a.v."
        />
        <datalist id="org_attention_list">
          <option value="De heer" />
          <option value="Mevrouw" />
          <option value="T.a.v." />
        </datalist>

        <label style={{ fontSize: 13, color: "#333" }}>Naam</label>
        <input
          ref={nameRef}
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          onBlur={() =>
            setForm((prev) => ({
              ...prev,
              name: formatNameLike(prev.name),
            }))
          }
          style={inputStyle}
        />

        <label style={{ fontSize: 13, color: "#333" }}>Zoeknaam</label>
        <input
          ref={searchNameRef}
          value={form.search_name}
          onChange={(e) => {
            setTouched((x) => ({ ...x, search_name: true }));
            setForm({ ...form, search_name: e.target.value });
          }}
          style={inputStyle}
        />

        <hr style={{ margin: "10px 0" }} />
      </>
    ) : (
      <>
        <label style={{ fontSize: 13, color: "#333" }}>Naam</label>
        <input
          ref={nameRef}
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          onBlur={() =>
            setForm((prev) => ({
              ...prev,
              name: formatNameLike(prev.name),
            }))
          }
          style={inputStyle}
        />

        <label style={{ fontSize: 13, color: "#333" }}>Zoeknaam</label>
        <input
          ref={searchNameRef}
          value={form.search_name}
          onChange={(e) => {
            setTouched((x) => ({ ...x, search_name: true }));
            setForm({ ...form, search_name: e.target.value });
          }}
          style={inputStyle}
        />

        <label style={{ fontSize: 13, color: "#333" }}>Hoofdorganisatie</label>
        <select
          ref={parentRef}
          value={String(form.parent_organisation_id || "")}
          onChange={(e) =>
            setForm({
              ...form,
              parent_organisation_id: e.target.value === "" ? null : e.target.value
            })
          }
          style={selectStyle}
        >
          <option value="">(geen)</option>
          {orgs
            .filter((o) => String(o.id) !== String(form.id || ""))
            .map((o) => (
              <option key={o.id} value={String(o.id)}>
                {o.name || o.search_name || `#${o.id}`}
              </option>
            ))}
        </select>

        <hr style={{ margin: "10px 0" }} />
      </>
    )}

                <label style={{ fontSize: 13, color: "#333" }}>Soort relatie (Type)</label>
                <input
                  ref={typeRef}
                  list="org_type_list"
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                  onBlur={(e) => {
                    ensureOption(typeOptionsKey, e.target.value);
                    setOptionsTick((x) => x + 1);
                  }}
                  style={inputStyle}
                />
                <datalist id="org_type_list">{typeOptions.map((x) => <option key={x} value={x} />)}</datalist>

                <label style={{ fontSize: 13, color: "#333" }}>Administratie type (Deb/Cred/geen)</label>
                <input
                  ref={adminRef}
                  list="org_admin_list"
                  value={form.administration_type}
                  onChange={(e) => setForm({ ...form, administration_type: e.target.value })}
                  onBlur={(e) => {
                    ensureOption(adminOptionsKey, e.target.value);
                    setOptionsTick((x) => x + 1);
                  }}
                  style={inputStyle}
                />
                <datalist id="org_admin_list">{adminTypeOptions.map((x) => <option key={x} value={x} />)}</datalist>

                <label style={{ fontSize: 13, color: "#333" }}>Datum 1e contact</label>
                <input
                  ref={firstContactRef}
                  type="date"
                  value={form.first_contact_date || ""}
                  onChange={(e) => setForm({ ...form, first_contact_date: e.target.value })}
                  style={inputStyle}
                />

                <hr style={{ margin: "10px 0" }} />

                <label style={{ fontSize: 13, color: "#333" }}>Telefoon</label>
               <input
  ref={phoneRef}
  value={form.phone}
  onChange={(e) => {
    const v = e.target.value;
if (/^\+?[0-9-\s]*$/.test(v)) {
      setForm({ ...form, phone: v });
    }
  }}
  style={inputStyle}
/>

<label style={{ fontSize: 12, marginTop: 6, display: "block" }}>
  Mobiel
</label>
<input
  ref={mobileRef}
  value={form.mobile}
  onChange={(e) => {
    const v = e.target.value;
    if (/^\+?[0-9-\s]*$/.test(v)) {
      setForm({
        ...form,
        mobile: v,
        correspondence_whatsapp: safeTrim(v) ? !!form.correspondence_whatsapp : false,
      });
    }
  }}
  style={inputStyle}
/>

                <label style={{ fontSize: 13, color: "#333" }}>E-mail</label>
                <input
                  ref={emailRef}
                  value={form.email}
                  onChange={(e) => {
                    const v = e.target.value;
                    setForm({
                      ...form,
                      email: v,
                      correspondence_email: safeTrim(v) ? !!form.correspondence_email : false,
                    });
                  }}
                  style={inputStyle}
                />

                <label style={{ fontSize: 13, color: "#333" }}>Internet adres</label>
                <input ref={webRef} value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} style={inputStyle} />

                <hr style={{ margin: "10px 0" }} />

                <div style={{ fontSize: 13, fontWeight: 700, opacity: 0.75, marginTop: 2 }}>Bezoekadres</div>

                <label style={{ fontSize: 13, color: "#333" }}>Straat</label>
                <input ref={vStreetRef} value={form.visit_street} onChange={(e) => setForm({ ...form, visit_street: e.target.value })} style={inputStyle} />

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                  <div>
                    <label style={{ fontSize: 13, color: "#333" }}>Huisnummer</label>
                    <input ref={vHouseRef} value={form.visit_house_number} onChange={(e) => setForm({ ...form, visit_house_number: intOrEmptyString(e.target.value) })} style={inputStyle} />
                  </div>
                  <div>
                    <label style={{ fontSize: 13, color: "#333" }}>Toevoeging</label>
                    <input ref={vAddRef} value={form.visit_addition} onChange={(e) => setForm({ ...form, visit_addition: e.target.value })} style={inputStyle} />
                  </div>
                  <div />
                </div>

                <label style={{ fontSize: 13, color: "#333" }}>Postcode (keuzelijst)</label>
                <input
                  ref={vPcRef}
                  list="org_visit_pc_list"
                  value={form.visit_postcode}
                  onChange={(e) => setForm({ ...form, visit_postcode: e.target.value })}
                  onBlur={(e) => {
                    ensureOption(pcOptionsKey, e.target.value);
                    setOptionsTick((x) => x + 1);
                  }}
                  style={inputStyle}
                />
                <datalist id="org_visit_pc_list">{postcodeOptions.map((x) => <option key={x} value={x} />)}</datalist>

                <label style={{ fontSize: 13, color: "#333" }}>Plaats (keuzelijst)</label>
                <input
                  ref={vCityRef}
                  list="org_visit_city_list"
                  value={form.visit_city}
                  onChange={(e) => setForm({ ...form, visit_city: e.target.value })}
                  onBlur={(e) => {
                    ensureOption(cityOptionsKey, e.target.value);
                    setOptionsTick((x) => x + 1);
                  }}
                  style={inputStyle}
                />
                <datalist id="org_visit_city_list">{cityOptions.map((x) => <option key={x} value={x} />)}</datalist>

                <label style={{ fontSize: 13, color: "#333" }}>Provincie (keuzelijst)</label>
                <input
                  ref={vProvRef}
                  list="org_visit_prov_list"
                  value={form.visit_province}
                  onChange={(e) => setForm({ ...form, visit_province: e.target.value })}
                  onBlur={(e) => {
                    ensureOption(provOptionsKey, e.target.value);
                    setOptionsTick((x) => x + 1);
                  }}
                  style={inputStyle}
                />
                <datalist id="org_visit_prov_list">{provinceOptions.map((x) => <option key={x} value={x} />)}</datalist>

                <label style={{ fontSize: 13, color: "#333" }}>Regio (keuzelijst)</label>
                <input
                  ref={vRegionRef}
                  list="org_visit_region_list"
                  value={form.visit_region}
                  onChange={(e) => setForm({ ...form, visit_region: e.target.value })}
                  onBlur={(e) => {
                    ensureOption(regionOptionsKey, e.target.value);
                    setOptionsTick((x) => x + 1);
                  }}
                  style={inputStyle}
                />
                <datalist id="org_visit_region_list">{regionOptions.map((x) => <option key={x} value={x} />)}</datalist>

                <label style={{ fontSize: 13, color: "#333" }}>Land (keuzelijst)</label>
                <input
                  ref={vCountryRef}
                  list="org_visit_country_list"
                  value={form.visit_country}
                  onChange={(e) => setForm({ ...form, visit_country: e.target.value })}
                  onBlur={(e) => {
                    ensureOption(countryOptionsKey, e.target.value);
                    setOptionsTick((x) => x + 1);
                  }}
                  style={inputStyle}
                />
                <datalist id="org_visit_country_list">{countryOptions.map((x) => <option key={x} value={x} />)}</datalist>

                <hr style={{ margin: "10px 0" }} />

                <div style={{ fontSize: 13, fontWeight: 700, opacity: 0.75, marginTop: 2 }}>Correspondentieadres</div>

                <label style={{ fontSize: 13, color: "#333" }}>Straat</label>
                <input
                  ref={cStreetRef}
                  value={form.corr_street}
                  onChange={(e) => {
                    setTouchedCorr((x) => ({ ...x, corr_street: true }));
                    setForm({ ...form, corr_street: e.target.value });
                  }}
                  style={inputStyle}
                />

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                  <div>
                    <label style={{ fontSize: 13, color: "#333" }}>Huisnummer</label>
                    <input
                      ref={cHouseRef}
                      value={form.corr_house_number}
                      onChange={(e) => {
                        setTouchedCorr((x) => ({ ...x, corr_house_number: true }));
                        setForm({ ...form, corr_house_number: intOrEmptyString(e.target.value) });
                      }}
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: 13, color: "#333" }}>Toevoeging</label>
                    <input
                      ref={cAddRef}
                      value={form.corr_addition}
                      onChange={(e) => {
                        setTouchedCorr((x) => ({ ...x, corr_addition: true }));
                        setForm({ ...form, corr_addition: e.target.value });
                      }}
                      style={inputStyle}
                    />
                  </div>
                  <div />
                </div>

                <label style={{ fontSize: 13, color: "#333" }}>Postcode (keuzelijst)</label>
                <input
                  ref={cPcRef}
                  list="org_corr_pc_list"
                  value={form.corr_postcode}
                  onChange={(e) => {
                    setTouchedCorr((x) => ({ ...x, corr_postcode: true }));
                    setForm({ ...form, corr_postcode: e.target.value });
                  }}
                  onBlur={(e) => {
                    ensureOption(pcOptionsKey, e.target.value);
                    setOptionsTick((x) => x + 1);
                  }}
                  style={inputStyle}
                />
                <datalist id="org_corr_pc_list">{postcodeOptions.map((x) => <option key={x} value={x} />)}</datalist>

                <label style={{ fontSize: 13, color: "#333" }}>Plaats (keuzelijst)</label>
                <input
                  ref={cCityRef}
                  list="org_corr_city_list"
                  value={form.corr_city}
                  onChange={(e) => {
                    setTouchedCorr((x) => ({ ...x, corr_city: true }));
                    setForm({ ...form, corr_city: e.target.value });
                  }}
                  onBlur={(e) => {
                    ensureOption(cityOptionsKey, e.target.value);
                    setOptionsTick((x) => x + 1);
                  }}
                  style={inputStyle}
                />
                <datalist id="org_corr_city_list">{cityOptions.map((x) => <option key={x} value={x} />)}</datalist>

                <label style={{ fontSize: 13, color: "#333" }}>Provincie (keuzelijst)</label>
                <input
                  ref={cProvRef}
                  list="org_corr_prov_list"
                  value={form.corr_province}
                  onChange={(e) => {
                    setTouchedCorr((x) => ({ ...x, corr_province: true }));
                    setForm({ ...form, corr_province: e.target.value });
                  }}
                  onBlur={(e) => {
                    ensureOption(provOptionsKey, e.target.value);
                    setOptionsTick((x) => x + 1);
                  }}
                  style={inputStyle}
                />
                <datalist id="org_corr_prov_list">{provinceOptions.map((x) => <option key={x} value={x} />)}</datalist>

                <label style={{ fontSize: 13, color: "#333" }}>Regio (keuzelijst)</label>
                <input
                  ref={cRegionRef}
                  list="org_corr_region_list"
                  value={form.corr_region}
                  onChange={(e) => {
                    setTouchedCorr((x) => ({ ...x, corr_region: true }));
                    setForm({ ...form, corr_region: e.target.value });
                  }}
                  onBlur={(e) => {
                    ensureOption(regionOptionsKey, e.target.value);
                    setOptionsTick((x) => x + 1);
                  }}
                  style={inputStyle}
                />
                <datalist id="org_corr_region_list">{regionOptions.map((x) => <option key={x} value={x} />)}</datalist>

                <label style={{ fontSize: 13, color: "#333" }}>Land (keuzelijst)</label>
                <input
                  ref={cCountryRef}
                  list="org_corr_country_list"
                  value={form.corr_country}
                  onChange={(e) => {
                    setTouchedCorr((x) => ({ ...x, corr_country: true }));
                    setForm({ ...form, corr_country: e.target.value });
                  }}
                  onBlur={(e) => {
                    ensureOption(countryOptionsKey, e.target.value);
                    setOptionsTick((x) => x + 1);
                  }}
                  style={inputStyle}
                />
                <datalist id="org_corr_country_list">{countryOptions.map((x) => <option key={x} value={x} />)}</datalist>

                <hr style={{ margin: "10px 0" }} />

                <label
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "center",
                    opacity: safeTrim(form.mobile) ? 1 : 0.55,
                  }}
                  title={safeTrim(form.mobile) ? "" : "Mobiel is leeg; WhatsApp-akkoord is niet beschikbaar."}
                >
                  <input
                    ref={correspondenceWhatsappRef}
                    type="checkbox"
                    checked={safeTrim(form.mobile) ? !!form.correspondence_whatsapp : false}
                    disabled={!safeTrim(form.mobile)}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        correspondence_whatsapp: safeTrim(form.mobile) ? e.target.checked : false,
                      })
                    }
                  />
                  Correspondentie en akkoord via WhatsApp (j/n)
                </label>

                <label
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "center",
                    opacity: safeTrim(form.email) ? 1 : 0.55,
                  }}
                  title={safeTrim(form.email) ? "" : "E-mail is leeg; e-mailakkoord is niet beschikbaar."}
                >
                  <input
                    ref={correspondenceEmailRef}
                    type="checkbox"
                    checked={safeTrim(form.email) ? !!form.correspondence_email : false}
                    disabled={!safeTrim(form.email)}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        correspondence_email: safeTrim(form.email) ? e.target.checked : false,
                      })
                    }
                  />
                  Correspondentie en akkoord via E-mail (j/n)
                </label>

                <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input ref={invoiceRef} type="checkbox" checked={!!form.invoice_digital} onChange={(e) => setForm({ ...form, invoice_digital: e.target.checked })} />
                  Ontvangt facturen digitaal (j/n)
                </label>

                <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input ref={mailingRef} type="checkbox" checked={!!form.mailing_list} onChange={(e) => setForm({ ...form, mailing_list: e.target.checked })} />
                  Mailinglijst (j/n)
                </label>

                <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input ref={activeRef} type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                  Actief (j/n)
                </label>

                {isCompanyRelation && (
                  <>
                    <hr style={{ margin: "10px 0" }} />

                    <div style={{ fontSize: 13, fontWeight: 700, opacity: 0.75, marginTop: 2 }}>Tarieven</div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 96px", gap: 8, alignItems: "end" }}>
                      <div>
                        <label style={{ fontSize: 13, color: "#333" }}>Junior</label>
                        <input
                          ref={rateJuniorRef}
                          value={(form.rate_junior || "").toString().replace(".", ",")}
                          onChange={(e) => setRateAmount("junior", e.target.value)}
                          onBlur={() => blurRateAmount("junior")}
                          placeholder="€ 0,00"
                          style={inputStyle}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: 13, color: "#333" }}>%</label>
                        <input
                          ref={rateJuniorPercentRef}
                          value={(form.rate_junior_percent || "100.00").toString().replace(".", ",")}
                          readOnly
                          style={{ ...inputStyle, background: "#f7f7f7" }}
                        />
                      </div>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 96px", gap: 8, alignItems: "end", marginTop: 6 }}>
                      <div>
                        <label style={{ fontSize: 13, color: "#333" }}>Medior</label>
                        <input
                          ref={rateMediorRef}
                          value={(form.rate_medior || "").toString().replace(".", ",")}
                          onChange={(e) => setRateAmount("medior", e.target.value)}
                          onBlur={() => blurRateAmount("medior")}
                          placeholder="€ 0,00"
                          style={inputStyle}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: 13, color: "#333" }}>%</label>
                        <input
                          ref={rateMediorPercentRef}
                          value={(form.rate_medior_percent || "").toString().replace(".", ",")}
                          onChange={(e) => setRatePercent("medior", e.target.value)}
                          onBlur={() => blurRatePercent("medior")}
                          placeholder="%"
                          style={inputStyle}
                        />
                      </div>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 96px", gap: 8, alignItems: "end", marginTop: 6 }}>
                      <div>
                        <label style={{ fontSize: 13, color: "#333" }}>Senior</label>
                        <input
                          ref={rateSeniorRef}
                          value={(form.rate_senior || "").toString().replace(".", ",")}
                          onChange={(e) => setRateAmount("senior", e.target.value)}
                          onBlur={() => blurRateAmount("senior")}
                          placeholder="€ 0,00"
                          style={inputStyle}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: 13, color: "#333" }}>%</label>
                        <input
                          ref={rateSeniorPercentRef}
                          value={(form.rate_senior_percent || "").toString().replace(".", ",")}
                          onChange={(e) => setRatePercent("senior", e.target.value)}
                          onBlur={() => blurRatePercent("senior")}
                          placeholder="%"
                          style={inputStyle}
                        />
                      </div>
                    </div>

                    <div style={{ height: 8 }} />

                    <label style={{ fontSize: 13, color: "#333" }}>Verkoopfactor o.b.v. salaris</label>
                    <input
                      ref={salesRef}
                      value={(form.sales_factor || "").toString().replace(".", ",")}
                      onChange={(e) => setForm({ ...form, sales_factor: e.target.value.replace(",", ".") })}
                      style={inputStyle}
                    />
                  </>
                )}

                <hr style={{ margin: "10px 0" }} />

                <div style={{ fontSize: 13, fontWeight: 700, opacity: 0.75, marginTop: 2 }}>Bank / betaling</div>

                <label style={{ fontSize: 13, color: "#333" }}>Bank (keuzelijst)</label>
                <input
                  ref={bankRef}
                  list="org_bank_list"
                  value={form.bank}
                  onChange={(e) => setForm({ ...form, bank: e.target.value })}
                  onBlur={(e) => {
                    ensureOption(bankOptionsKey, e.target.value);
                    setOptionsTick((x) => x + 1);
                  }}
                  style={inputStyle}
                />
                <datalist id="org_bank_list">{bankOptions.map((x) => <option key={x} value={x} />)}</datalist>

                <label style={{ fontSize: 13, color: "#333" }}>IBAN</label>
                <input ref={ibanRef} value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} style={inputStyle} />

                <label style={{ fontSize: 13, color: "#333" }}>BIC</label>
                <input ref={bicRef} value={form.bic} onChange={(e) => setForm({ ...form, bic: e.target.value })} style={inputStyle} />

                <label style={{ fontSize: 13, color: "#333" }}>Ten name van</label>
                <input
                  ref={accNameRef}
                  value={form.account_name}
                  onChange={(e) => {
                    setTouched((x) => ({ ...x, account_name: true }));
                    setForm({ ...form, account_name: e.target.value });
                  }}
                  style={inputStyle}
                />

{isCompanyRelation && (
                  <>
                    <label style={{ fontSize: 13, color: "#333" }}>G-rekening (IBAN)</label>
                    <input ref={gIbanRef} value={form.g_iban} onChange={(e) => setForm({ ...form, g_iban: e.target.value })} style={inputStyle} />

                    <label style={{ fontSize: 13, color: "#333" }}>% G-rekening (integer)</label>
                    <input ref={gPercRef} value={form.g_percentage} onChange={(e) => setForm({ ...form, g_percentage: intOrEmptyString(e.target.value) })} style={inputStyle} />

                    <hr style={{ margin: "10px 0" }} />

                    <div style={{ fontSize: 13, fontWeight: 700, opacity: 0.75, marginTop: 2 }}>Fiscaal</div>

                    <label style={{ fontSize: 13, color: "#333" }}>KvK</label>
                    <input ref={kvkRef} value={form.kvk} onChange={(e) => setForm({ ...form, kvk: e.target.value })} style={inputStyle} />

                    <label style={{ fontSize: 13, color: "#333" }}>BTW nummer</label>
                    <input ref={vatNrRef} value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} style={inputStyle} />

                    <label style={{ fontSize: 13, color: "#333" }}>BTW tarief (keuzelijst)</label>
                    <input
                      ref={vatRateRef}
                      list="org_vatrate_list"
                      value={form.vat_rate}
                      onChange={(e) => setForm({ ...form, vat_rate: e.target.value })}
                      onBlur={(e) => {
                        ensureOption(vatRateOptionsKey, e.target.value);
                        setOptionsTick((x) => x + 1);
                      }}
                      style={inputStyle}
                    />
                    <datalist id="org_vatrate_list">{vatRateOptions.map((x) => <option key={x} value={x} />)}</datalist>

                    <hr style={{ margin: "10px 0" }} />
                  </>
                )}

                <div style={{ fontSize: 13, fontWeight: 700, opacity: 0.75, marginTop: 2 }}>Betaling</div>

                <label style={{ fontSize: 13, color: "#333" }}>Betaalvoorwaarden (keuzelijst)</label>
                <input
                  ref={payTermsRef}
                  list="org_payterms_list"
                  value={form.payment_terms}
                  onChange={(e) => setForm({ ...form, payment_terms: e.target.value })}
                  onBlur={(e) => {
                    ensureOption(payTermsOptionsKey, e.target.value);
                    setOptionsTick((x) => x + 1);
                  }}
                  style={inputStyle}
                />
                <datalist id="org_payterms_list">{paymentTermsOptions.map((x) => <option key={x} value={x} />)}</datalist>

                <label style={{ fontSize: 13, color: "#333" }}>Betaalwijze (keuzelijst)</label>
                <input
                  ref={payMethodRef}
                  list="org_paymethod_list"
                  value={form.payment_method}
                  onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
                  onBlur={(e) => {
                    ensureOption(payMethodOptionsKey, e.target.value);
                    setOptionsTick((x) => x + 1);
                  }}
                  style={inputStyle}
                />
                <datalist id="org_paymethod_list">{paymentMethodOptions.map((x) => <option key={x} value={x} />)}</datalist>

                <label style={{ fontSize: 13, color: "#333" }}>Betaaltermijn (keuzelijst)</label>
                <input
  ref={payTermRef}
  list="org_payterm_list"
  type="number"
  value={form.payment_term_days || ""}
  onChange={(e) => setForm({ ...form, payment_term_days: e.target.value })}
  onBlur={(e) => {
    ensureOption(payTermOptionsKey, e.target.value);
    setOptionsTick((x) => x + 1);
  }}
  style={inputStyle}
/>
                <datalist id="org_payterm_list">{paymentTermOptions.map((x) => <option key={x} value={x} />)}</datalist>

                <label style={{ fontSize: 13, color: "#333" }}>Kredietlimiet (integer)</label>
                <input ref={creditRef} value={form.credit_limit} onChange={(e) => setForm({ ...form, credit_limit: intOrEmptyString(e.target.value) })} style={inputStyle} />

                <hr style={{ margin: "10px 0" }} />

                <label style={{ fontSize: 13, color: "#333" }}>Opmerking</label>
                <textarea
                  ref={remarkSmallRef}
                  value={form.remarks}
                  onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                  rows={6}
                  style={{ ...inputStyle, resize: "vertical" }}
                  onFocus={() => {
                    setMode("form");

                    // ✅ als focus door onze code is gezet (terug/klein maken), dan NIET groot openen
                    if (ignoreNextRemarkSmallFocusRef.current) {
                      ignoreNextRemarkSmallFocusRef.current = false;
                      return;
                    }

                    // ✅ user focus -> groot openen + cursor op einde (nieuwe regel klaarzetten)
                    openRemarkEditorAndFocusEnd();
                  }}
                />

<div
  style={{
    background: "white",
    paddingTop: 10,
    marginTop: 8,
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 10,
    borderTop: "1px solid #eee",
  }}
>
<button
  onClick={saveItem}
  disabled={isFormLocked}
  style={
    isFormLocked
      ? { ...buttonPrimary("#2f7dd1"), opacity: 0.5, cursor: "not-allowed" }
      : buttonPrimary("#2f7dd1")
  }
>
  💾 Opslaan
</button>

<button
  onClick={deleteItem}
  disabled={isFormLocked || !deleteEnabled}
  style={
    isFormLocked || !deleteEnabled
      ? { ...buttonPrimary("#d64545"), opacity: 0.5, cursor: "not-allowed" }
      : buttonPrimary("#d64545")
  }
>
  🗑 Verwijderen
</button>

<button
  onClick={openMatchingBlock}
  disabled={!formHasId()}
  style={
    !formHasId()
      ? { ...buttonPrimary("#198754"), opacity: 0.5, cursor: "not-allowed" }
      : buttonPrimary("#198754")
  }
>
  🧩 Matching
</button>

<button
  type="button"
  onClick={vernieuwList}
  style={buttonStyle}
>
  🔄 Vernieuw
</button>

</div>

                {matchingOpen && (
                  <>
                    <hr style={{ margin: "10px 0" }} />

                    <div
                      ref={matchingSectionRef}
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        opacity: 0.75,
                        marginTop: 8,
                      }}
                    >
                      Matching gegevens
                    </div>

                    <div style={{ marginTop: 8, display: "grid", gap: 6 }}>
                      {sortedMatchingRows.map((m) => (
                        <div
                          key={m.id}
                          style={{
                            border: "1px solid #e2e2e2",
                            borderRadius: 6,
                            padding: 8,
                            fontSize: 13,
                            background: "#fafafa",
                          }}
                        >
                          <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                            <b>{m.category || "-"}</b>

                            <div style={{ display: "flex", gap: 4 }}>
                              <button
                                onClick={() => startEditMatchingRow(m)}
                                style={{
                                  border: "none",
                                  background: "transparent",
                                  cursor: "pointer",
                                  color: "#1f4e79",
                                }}
                                title="Wijzigen"
                              >
                                ✏️
                              </button>

                              <button
                                onClick={() => deleteMatchingRow(m.id)}
                                style={{
                                  border: "none",
                                  background: "transparent",
                                  cursor: "pointer",
                                  color: "#b00020",
                                }}
                                title="Verwijderen"
                              >
                                🗑
                              </button>
                            </div>
                          </div>

                          <div>{m.name || "-"}</div>
                          {m.description && <div style={{ opacity: 0.8 }}>{m.description}</div>}

                          <div style={{ opacity: 0.75 }}>
                            {m.level ? `Niveau: ${m.level}` : ""}
                            {m.diploma ? " · Diploma" : ""}
                          </div>

                          <div style={{ opacity: 0.75 }}>
                            {[formatDateNL(m.date_from), formatDateNL(m.date_to)].filter(Boolean).join(" t/m ")}
                          </div>

                          {m.employee && <div style={{ opacity: 0.75 }}>Werknemer: {m.employee}</div>}
                        </div>
                      ))}

                      {matchingRows.length === 0 && (
                        <div style={{ fontSize: 12, opacity: 0.7 }}>
                          Nog geen matchinggegevens.
                        </div>
                      )}
                    </div>

                    <div ref={matchingFormRef} style={{ marginTop: 12, display: "grid", gap: 6 }}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 700,
                          opacity: 0.75,
                          marginTop: 8,
                        }}
                      >
                        {matchingEditId ? "Matchingregel wijzigen" : "Nieuwe matchingregel toevoegen"}
                      </div>

                      <label style={{ fontSize: 13, color: "#333" }}>Categorie</label>
                      <input
                        ref={matchingInputRef}
                        value={matchingDraft.category}
                        onChange={(e) => setMatchingDraft({ ...matchingDraft, category: e.target.value })}
                        style={inputStyle}
                        list="org_matching_category_list"
                      />

                      <datalist id="org_matching_category_list">
                        {matchingCategoryOptions.map((x) => <option key={x} value={x} />)}
                      </datalist>

                      <label style={{ fontSize: 13, color: "#333" }}>Naam</label>
                      <input
                        value={matchingDraft.name}
                        onChange={(e) => setMatchingDraft({ ...matchingDraft, name: e.target.value })}
                        style={inputStyle}
                      />

                      <label style={{ fontSize: 13, color: "#333" }}>Omschrijving</label>
                      <textarea
                        value={matchingDraft.description}
                        onChange={(e) => setMatchingDraft({ ...matchingDraft, description: e.target.value })}
                        rows={3}
                        style={{ ...inputStyle, resize: "vertical" }}
                      />

                      <label style={{ fontSize: 13, color: "#333" }}>Niveau</label>
                      <input
                        value={matchingDraft.level}
                        onChange={(e) => setMatchingDraft({ ...matchingDraft, level: e.target.value })}
                        style={inputStyle}
                      />

                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                        <div>
                          <label style={{ fontSize: 13, color: "#333" }}>Datum van</label>
                          <input
                            type="date"
                            value={matchingDraft.date_from || ""}
                            onChange={(e) => setMatchingDraft({ ...matchingDraft, date_from: e.target.value })}
                            style={inputStyle}
                          />
                        </div>

                        <div>
                          <label style={{ fontSize: 13, color: "#333" }}>Datum t/m</label>
                          <input
                            type="date"
                            value={matchingDraft.date_to || ""}
                            onChange={(e) => setMatchingDraft({ ...matchingDraft, date_to: e.target.value })}
                            style={inputStyle}
                          />
                        </div>
                      </div>

                      <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13 }}>
                        <input
                          type="checkbox"
                          checked={matchingDraft.diploma === true}
                          onChange={(e) => setMatchingDraft({ ...matchingDraft, diploma: e.target.checked })}
                        />
                        Diploma
                      </label>

                      <label style={{ fontSize: 13, color: "#333" }}>Werknemer</label>
                      <input
                        value={matchingDraft.employee}
                        onChange={(e) => setMatchingDraft({ ...matchingDraft, employee: e.target.value })}
                        style={inputStyle}
                      />

                      <div style={{ display: "flex", gap: 8, marginTop: 4, flexWrap: "wrap" }}>
                        <button
                          onClick={saveMatchingRow}
                          disabled={isReadonlyRole}
                          style={
                            isReadonlyRole
                              ? { ...buttonPrimary("#2f7dd1"), opacity: 0.5, cursor: "not-allowed" }
                              : buttonPrimary("#2f7dd1")
                          }
                        >
                          💾 Matching opslaan
                        </button>

                        <button
                          onClick={matchingEditId ? cancelEditMatchingRow : closeMatchingBlock}
                          style={buttonStyle}
                        >
                          Annuleren
                        </button>
                      </div>
                    </div>
                  </>
                )}

                <div style={{ marginTop: 10, opacity: 0.7, fontSize: 12 }}>
                  Organisatie-id: {formHasId() ? String(form.id) : "(nieuw)"}
                </div>
              </div>
            </fieldset>
            )}
          </div>
        </div>
      </div>


{aiOpen && (
        <div
          onClick={closeAiPopup}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 10,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: isMobile ? "96vw" : 980,
              height: isMobile ? "88vh" : "90vh",
              overflow: "hidden",
              background: "#fff",
              borderRadius: 12,
              boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
              padding: 16,
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
            }}
          >

<div
  style={{
    position: "sticky",
    top: 0,
    zIndex: 10,
    background: "#fff",
    paddingBottom: 10,
    marginBottom: 12,
    borderBottom: "1px solid #ddd",

    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  }}
>
              <h2 style={{ margin: 0 }}>AI Organisaties</h2>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  onClick={printAiDocument}
                  disabled={aiBusy}
                  style={buttonStyle}
                >
                  Printen
                </button>

                <button
                  onClick={startAiSpeak}
                  disabled={!voice.supported || voice.listening || aiBusy}
                  style={
                    !voice.supported || voice.listening || aiBusy
                      ? buttonDisabledStyle
                      : buttonStyle
                  }
                >
                  {voice.listening ? "🎤 Luistert…" : "🎤 Spreek"}
                </button>

                <button
                  onClick={closeAiPopup}
                  disabled={aiBusy}
                  style={buttonStyle}
                >
                  Sluiten
                </button>
              </div>
            </div>

<div style={{ marginBottom: 12, fontWeight: 700 }}>
              Kies een actie:
            </div>

<div
  style={{
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr 1fr",
    gap: 8,
    marginBottom: 16,
  }}
>
<button
    onClick={runAiTextAnalyseUnified}
    disabled={aiBusy}
    style={aiBusy ? buttonDisabledStyle : buttonStyle}
  >
    Tekst analyseren
  </button>

  <button
    onClick={runAiOrganisationCheckUnified}
    disabled={aiBusy}
    style={aiBusy ? buttonDisabledStyle : buttonStyle}
  >
    Organisatie controleren
  </button>

  <button
    onClick={runAiSummary}
    disabled={aiBusy}
    style={aiBusy ? buttonDisabledStyle : buttonStyle}
  >
    Dossieroverzicht
  </button>

  <button
    onClick={runAiChat}
    disabled={aiBusy}
    style={aiBusy ? buttonDisabledStyle : buttonStyle}
  >
    Chat met AI
  </button>
</div>

<div style={{ marginBottom: 8, fontWeight: 700, flex: "0 0 auto" }}>
              AI venster
            </div>

            <textarea
              ref={aiTextRef}
              value={aiText}
              onChange={(e) => setAiText(e.target.value)}
              placeholder="Plak hier tekst voor Tekst analyseren, of typ hier je vraag voor Chat met AI."
              style={{
                width: "100%",
                flex: 1,
                minHeight: 0,
                height: "100%",
                padding: 12,
                border: "1px solid #ccc",
                borderRadius: 8,
                fontFamily: "inherit",
                fontSize: 15,
                lineHeight: 1.45,
                boxSizing: "border-box",
                resize: "none",
                whiteSpace: "pre-wrap",
                overflowY: "auto",
              }}
            />

          </div>
        </div>
      )}
    </div>
  );
}